"use client";

// src/components/AspectRatio/AspectRatio.react.tsx
import * as AspectRatioPrimitive from "@radix-ui/react-aspect-ratio";
import React, { forwardRef } from "react";
var AspectRatio = forwardRef(function AspectRatio2({ ratio, ...rest }, ref) {
  if (typeof ratio === "number") {
    return /* @__PURE__ */ React.createElement(AspectRatioPrimitive.Root, { ratio, ref, ...rest });
  }
  const [width, height] = ratio.includes(":") ? ratio.split(":") : ratio.split("/");
  const parsedRatio = (Number(width) || 1) / (Number(height) || 1);
  return /* @__PURE__ */ React.createElement(AspectRatioPrimitive.Root, { ratio: parsedRatio, ref, ...rest });
});

// src/components/AuthCode/AuthCode.react.tsx
import React10, { forwardRef as forwardRef9, useRef as useRef2, useState } from "react";

// src/utils/classNames.ts
import { clsx } from "clsx";

// src/utils/twMerge.ts
import { extendTailwindMerge } from "tailwind-merge";

// src/constants/zIndex.ts
var current = 0;
var get = () => {
  current += 100;
  return current.toString();
};
var Z_INDEX = {
  sidebar: get(),
  overlay: get(),
  sheet: get(),
  modal: get(),
  tooltip: get(),
  toast: get()
};

// src/utils/twMerge.ts
var twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: [
            "xs",
            "sm",
            "md",
            "heading-xs",
            "heading-sm",
            "heading-md",
            "heading-lg",
            "display-sm",
            "display-md",
            "display-lg",
            "display-xl"
          ]
        }
      ],
      z: [
        {
          z: Object.keys(Z_INDEX)
        }
      ],
      "font-weight": ["font-inherit"],
      rounded: ["rounded-inherit", "rounded-initial"]
    }
  }
});

// src/utils/classNames.ts
var classNames = (...inputs) => {
  return twMerge(clsx(inputs));
};

// src/utils/twDataIdSelector.ts
var twDataIdSelector = (_componentWithDataId, arg) => arg;

// src/components/InlineFlex/InlineFlex.react.tsx
import React3, { forwardRef as forwardRef3 } from "react";

// src/components/Block/Block.react.tsx
import React2, { forwardRef as forwardRef2 } from "react";

// src/styles/utils/color.ts
import { tv as tv2 } from "tailwind-variants";

// src/utils/object.ts
var keys = (obj) => Object.keys(obj);
var fromEntries = (entries) => Object.fromEntries(entries);

// src/styles/tailwindBase.ts
import aspectRatio from "@tailwindcss/aspect-ratio";
import animate from "tailwindcss-animate";

// src/constants/breakpoints.ts
var breakpoints = {
  xs: 0,
  sm: 393,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
  "3xl": 1728,
  "4xl": 1920,
  max: 2560
};

// src/styles/tailwindBase.ts
var numberToPx = (n) => {
  return `${n}px`;
};
var COMMON_COLORS = {
  // General utility
  transparent: "transparent",
  current: "currentColor",
  inherit: "inherit",
  white: "rgb(var(--color-white) / <alpha-value>)",
  black: "rgb(var(--color-black) / <alpha-value>)",
  // Base background, but can be used for Text in certain situations (e.g inverse of primary text color)
  "base-1": "rgb(var(--color-base-1) / <alpha-value>)",
  "base-2": "rgb(var(--color-base-2) / <alpha-value>)",
  // Grays
  "gray-1": "rgb(var(--color-gray-1) / <alpha-value>)",
  "gray-2": "rgb(var(--color-gray-2) / <alpha-value>)",
  "gray-3": "rgb(var(--color-gray-3) / <alpha-value>)",
  // Blues
  "blue-1": "rgb(var(--color-blue-1) / <alpha-value>)",
  "blue-2": "rgb(var(--color-blue-2) / <alpha-value>)",
  "blue-3": "rgb(var(--color-blue-3) / <alpha-value>)",
  "blue-4": "rgb(var(--color-blue-4) / <alpha-value>)",
  "blue-5": "rgb(var(--color-blue-5) / <alpha-value>)",
  // Greens
  "green-1": "rgb(var(--color-green-1) / <alpha-value>)",
  "green-2": "rgb(var(--color-green-2) / <alpha-value>)",
  "green-3": "rgb(var(--color-green-3) / <alpha-value>)",
  // Yellows
  "yellow-1": "rgb(var(--color-yellow-1) / <alpha-value>)",
  "yellow-2": "rgb(var(--color-yellow-2) / <alpha-value>)",
  // Reds
  "red-1": "rgb(var(--color-red-1) / <alpha-value>)",
  "red-2": "rgb(var(--color-red-2) / <alpha-value>)",
  "red-3": "rgb(var(--color-red-3) / <alpha-value>)",
  // Accents
  "accent-red": "rgb(var(--color-accent-red) / <alpha-value>)",
  "accent-purple": "rgb(var(--color-accent-purple) / <alpha-value>)",
  "accent-fuchsia": "rgb(var(--color-accent-fuchsia) / <alpha-value>)",
  "accent-orange": "rgb(var(--color-accent-orange) / <alpha-value>)",
  "accent-cerulean": "rgb(var(--color-accent-cerulean) / <alpha-value>)",
  "accent-aqua": "rgb(var(--color-accent-aqua) / <alpha-value>)",
  // Aliases
  success: "rgb(var(--color-green-2) / <alpha-value>)",
  warning: "rgb(var(--color-yellow-1) / <alpha-value>)",
  error: "rgb(var(--color-red-2) / <alpha-value>)"
};
var TEXT_COLORS = {
  ...COMMON_COLORS,
  // Base text
  primary: "rgb(var(--color-text-primary) / <alpha-value>)",
  secondary: "rgb(var(--color-text-secondary) / <alpha-value>)",
  // Interactive text
  "interactive-primary": "rgb(var(--color-text-interactive-primary) / <alpha-value>)",
  "interactive-primary-hover": "rgb(var(--color-text-interactive-primary-hover) / <alpha-value>)",
  "interactive-secondary": "rgb(var(--color-text-interactive-secondary) / <alpha-value>)",
  "interactive-secondary-hover": "rgb(var(--color-text-interactive-secondary-hover) / <alpha-value>)",
  // Interactive icons
  "interactive-icon-primary": "rgb(var(--color-interactive-icon-primary) / <alpha-value>)",
  "interactive-icon-primary-hover": "rgb(var(--color-interactive-icon-primary-hover) / <alpha-value>)",
  "interactive-icon-secondary": "rgb(var(--color-interactive-icon-secondary) / <alpha-value>)",
  "interactive-icon-secondary-hover": "rgb(var(--color-interactive-icon-secondary-hover) / <alpha-value>)",
  // Used to match elevation background color for usages related to currentColor i.e. svgs
  "bg-elevation-1": "rgb(var(--color-elevation-1) / <alpha-value>)",
  "bg-elevation-1-subtle": "rgb(var(--color-elevation-1-subtle) / <alpha-value>)",
  "bg-elevation-2": "rgb(var(--color-elevation-2) / <alpha-value>)",
  "bg-elevation-3": "rgb(var(--color-elevation-3) / <alpha-value>)"
};
var BORDER_COLORS = {
  ...COMMON_COLORS,
  // Naming primarily to not conflict with border size presets
  "level-1": "var(--color-border-1)",
  "level-2": "var(--color-border-2)",
  "level-3": "var(--color-border-3)",
  "text-primary": "rgb(var(--color-text-primary) / <alpha-value>)",
  "text-secondary": "rgb(var(--color-text-secondary) / <alpha-value>)"
};
var tailwindBase = {
  content: [],
  darkMode: "class",
  theme: {
    colors: {
      ...COMMON_COLORS,
      // Component background grays
      "component-gray-1": "var(--color-component-gray-1)",
      "component-gray-2": "var(--color-component-gray-2)",
      "component-gray-3": "var(--color-component-gray-3)",
      "component-gray-4": "var(--color-component-gray-4)",
      "component-gray-1-solid": "rgb(var(--color-component-gray-1-solid) / <alpha-value>)",
      "component-gray-2-solid": "rgb(var(--color-component-gray-2-solid) / <alpha-value>)",
      // Elevation
      "elevation-1": "rgb(var(--color-elevation-1) / <alpha-value>)",
      "elevation-1-subtle": "rgb(var(--color-elevation-1-subtle) / <alpha-value>)",
      "elevation-2": "rgb(var(--color-elevation-2) / <alpha-value>)",
      "elevation-3": "rgb(var(--color-elevation-3) / <alpha-value>)",
      // In case we need to use border colors as background colors e.g. Separator
      "border-1": "var(--color-border-1)",
      "border-2": "var(--color-border-2)",
      "border-3": "var(--color-border-3)",
      // In case we need to use text colors as background colors, ex. underlines
      "text-primary": "rgb(var(--color-text-primary) / <alpha-value>)",
      "text-secondary": "rgb(var(--color-text-secondary) / <alpha-value>)"
    },
    textColor: TEXT_COLORS,
    borderColor: BORDER_COLORS,
    fill: TEXT_COLORS,
    boxShadow: {
      // Default tailwind shadow-none
      none: "box-shadow: 0 0 #0000",
      "elevation-1": "var(--shadow-elevation-1)",
      "elevation-1-subtle": "var(--shadow-elevation-1-subtle)",
      "elevation-2": "var(--shadow-elevation-2)",
      "elevation-3": "var(--shadow-elevation-3)"
    },
    fontFamily: {
      inter: "var(--font-family-inter)",
      "ibm-plex-mono": "var(--font-family-ibm-plex-mono)"
    },
    screens: {
      ...Object.fromEntries(
        Object.entries(breakpoints).map(([key, value]) => [
          key,
          numberToPx(value)
        ])
      ),
      // Fixed-range breakpoints
      "xs-sm": {
        min: numberToPx(breakpoints.xs),
        max: numberToPx(breakpoints.sm - 1)
      },
      "xs-lg": {
        min: numberToPx(breakpoints.xs),
        max: numberToPx(breakpoints.lg - 1)
      }
      // NOTE: Add more ranges here as needed (e.g. sm-md, xs-lg, etc.)
    },
    fontSize: {
      // Body
      xs: "12px",
      sm: "14px",
      md: "16px",
      // Heading
      "heading-xs": "18px",
      "heading-sm": "20px",
      "heading-md": "24px",
      "heading-lg": "32px",
      // Display
      "display-sm": "24px",
      "display-md": "32px",
      "display-lg": "48px",
      "display-xl": "60px"
    },
    aspectRatio: {
      auto: "auto",
      square: "1 / 1",
      video: "16 / 9",
      1: "1",
      2: "2",
      3: "3",
      4: "4",
      5: "5",
      6: "6",
      7: "7",
      8: "8",
      9: "9",
      10: "10",
      11: "11",
      12: "12",
      13: "13",
      14: "14",
      15: "15",
      16: "16"
    },
    extend: {
      borderRadius: {
        inherit: "inherit",
        initial: "initial",
        // This default value is in flight (e.g. in discussions to remove 10px entirely), but is what we had before as a "default"
        // so keeping it for now
        default: "10px"
      },
      fontWeight: {
        inherit: "inherit"
      },
      transitionProperty: {
        width: "width"
      },
      zIndex: Z_INDEX
    }
  },
  plugins: [aspectRatio, animate],
  safelist: [
    // Safelisting text colors so we can use them as `color` props in Components e.g. Text
    ...Object.keys(TEXT_COLORS).map((key) => `text-${key}`),
    // Safelisting fill colors so we can use them as `fill` props in Components e.g. SVGs
    ...Object.keys(TEXT_COLORS).map((key) => `fill-${key}`),
    // Safelisting border colors so we can use them as `color` props in Components e.g. Spinner
    ...Object.keys(BORDER_COLORS).map((key) => `border-${key}`),
    // Safelist aspect-ratio classes so they can be used dynamically at runtime (some uses require response from API)
    {
      pattern: /aspect-+/
    },
    // Safelist object fit classes to be used dynamically.
    ...[
      "object-contain",
      "object-cover",
      "object-fill",
      "object-none",
      "object-scale-down"
    ]
  ],
  future: {
    // This makes it such that hover styles are only applied when the user's browser supports hover e.g. desktop
    // https://github.com/tailwindlabs/tailwindcss/pull/8394#issue-1243295337
    hoverOnlyWhenSupported: true
  }
};

// src/styles/utils/interactive.ts
import { tv } from "tailwind-variants";
var interactiveVariants = tv({
  base: "cursor-pointer",
  variants: {
    text: {
      primary: "text-interactive-primary hover:text-interactive-primary-hover",
      secondary: "text-interactive-secondary hover:text-interactive-secondary-hover"
    },
    icon: {
      primary: "text-interactive-icon-primary hover:text-interactive-icon-primary-hover",
      secondary: "text-interactive-icon-secondary hover:text-interactive-icon-secondary-hover"
    },
    iconFill: {
      primary: "fill-interactive-icon-primary hover:fill-interactive-icon-primary-hover",
      secondary: "fill-interactive-icon-secondary hover:fill-interactive-icon-secondary-hover"
    }
  }
});

// src/styles/utils/color.ts
var allTailwindTextColors = fromEntries(
  keys(tailwindBase.theme.textColor).map((color) => [color, `text-${color}`])
);
var tailwindTextColors = allTailwindTextColors;
var textColor = {
  ...tailwindTextColors,
  // Special "color"s that handle the whole primary and secondary interactive styles
  "interactive-primary-styles": interactiveVariants({
    text: "primary"
  }),
  "interactive-secondary-styles": interactiveVariants({
    text: "secondary"
  }),
  "interactive-icon-primary-styles": interactiveVariants({
    icon: "primary"
  }),
  "interactive-icon-secondary-styles": interactiveVariants({
    icon: "secondary"
  })
};
var textColorVariants = tv2({
  variants: {
    color: textColor
  }
});
var borderColorVariants = tv2({
  variants: {
    color: fromEntries(
      keys(tailwindBase.theme.borderColor).map((color) => [
        color,
        `border-${color}`
      ])
    )
  }
});

// src/styles/utils/disabled.ts
import { tv as tv3 } from "tailwind-variants";
var disabledVariants = tv3({
  base: "disabled:pointer-events-none disabled:opacity-40",
  variants: {
    disabled: {
      true: "pointer-events-none opacity-40"
    }
  }
});

// src/styles/utils/input.ts
import { tv as tv4 } from "tailwind-variants";
var inputVariants = tv4({
  base: classNames(
    "flex h-10 w-full items-center rounded py-2 pl-4 pr-1 text-md",
    "border border-level-2 transition duration-200 hover:border-level-3",
    "dark:border-0 dark:bg-component-gray-1",
    "focus-within:shadow-none focus-within:hover:shadow-none focus-within:border-level-3",
    "focus:outline-none"
  ),
  variants: {
    disabled: {
      true: classNames(
        "pointer-events-none border-level-2 bg-component-gray-1 text-secondary dark:bg-component-gray-2"
      )
    },
    error: {
      true: "border-error"
    }
  }
});
var preventInputZoomStyles = classNames(
  "min-h-[26px] text-[16px] leading-[26px]",
  "sm:font-[inherit] sm:leading-[inherit] sm:text-[inherit]"
);

// src/styles/utils/link.ts
import { tv as tv5 } from "tailwind-variants";
var linkVariants = tv5({
  base: "cursor-pointer no-underline",
  variants: {
    variant: {
      default: interactiveVariants({ text: "primary" }),
      subtle: classNames(
        "font-semibold",
        interactiveVariants({ text: "secondary" })
      ),
      unstyled: ""
    }
  }
});

// src/styles/utils/objectFit.ts
import { tv as tv6 } from "tailwind-variants";
var objectFitVariants = tv6({
  variants: {
    objectFit: {
      contain: "object-contain",
      cover: "object-cover",
      fill: "object-fill",
      none: "object-none",
      "scale-down": "object-scale-down"
    }
  }
});

// src/styles/utils/overlay.ts
var overlayStyles = classNames(
  "fixed inset-0 z-overlay bg-gray-3/80",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
);

// src/components/Slot/index.ts
import { Slot } from "@radix-ui/react-slot";

// src/components/Block/Block.react.tsx
var Block = forwardRef2(function Block2({ asChild, color, className, ...rest }, ref) {
  const Comp = asChild ? Slot : "div";
  return /* @__PURE__ */ React2.createElement(
    Comp,
    {
      className: classNames(textColorVariants({ color }), className),
      ref,
      ...rest
    }
  );
});

// src/components/InlineFlex/InlineFlex.react.tsx
var InlineFlex = forwardRef3(
  function InlineFlex2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React3.createElement(
      Block,
      {
        className: classNames("inline-flex", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/Input/Input.react.tsx
import useMergedRef from "@react-hook/merged-ref";
import React9, {
  forwardRef as forwardRef8,
  useMemo,
  useRef
} from "react";

// src/icons/material-symbols/outlined/Close.react.tsx
import React4 from "react";

// src/styles/utils/fill.ts
import { tv as tv7 } from "tailwind-variants";
var allTailwindFills = fromEntries(
  keys(tailwindBase.theme.fill).map((fill2) => [fill2, `fill-${fill2}`])
);
var tailwindFills = allTailwindFills;
var fill = {
  ...tailwindFills,
  // Special "fill"s that handle the whole primary and secondary interactive styles
  "interactive-icon-primary-styles": interactiveVariants({
    iconFill: "primary"
  }),
  "interactive-icon-secondary-styles": interactiveVariants({
    iconFill: "secondary"
  })
};
var fillVariants = tv7({
  variants: {
    fill
  }
});

// src/icons/material-symbols/outlined/Close.react.tsx
var Close = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Close",
  ...rest
}) => {
  return /* @__PURE__ */ React4.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React4.createElement("title", null, title),
    /* @__PURE__ */ React4.createElement("path", { d: "m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" })
  );
};

// src/components/Flex/Flex.react.tsx
import React5, { forwardRef as forwardRef4 } from "react";
var Flex = forwardRef4(function Flex2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React5.createElement(Block, { className: classNames("flex", className), ref, ...rest });
});

// src/components/UnstyledButton/UnstyledButton.react.tsx
import { Slot as Slot2 } from "@radix-ui/react-slot";
import React6, { forwardRef as forwardRef5 } from "react";
var UnstyledButtonDataId = "UnstyledButton";
var UnstyledButton = Object.assign(
  forwardRef5(function UnstyledButton2({ asChild, className, ...rest }, ref) {
    const Comp = asChild ? Slot2 : "button";
    return /* @__PURE__ */ React6.createElement(
      Comp,
      {
        className: classNames(
          "inline-flex items-center border-0 bg-inherit font-[inherit] text-[100%]",
          className
        ),
        "data-id": UnstyledButtonDataId,
        ref,
        type: "button",
        ...rest
      }
    );
  }),
  { dataId: UnstyledButtonDataId }
);

// src/components/VerticalAligned/VerticalAligned.react.tsx
import React8, { forwardRef as forwardRef7 } from "react";

// src/components/FlexColumn/FlexColumn.react.tsx
import React7, { forwardRef as forwardRef6 } from "react";
var FlexColumn = forwardRef6(
  function FlexColumn2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React7.createElement(Flex, { className: classNames("flex-col", className), ref, ...rest });
  }
);

// src/components/VerticalAligned/VerticalAligned.react.tsx
var VerticalAligned = forwardRef7(
  function VerticalAligned2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React8.createElement(
      FlexColumn,
      {
        className: classNames("justify-center", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/Input/Input.react.tsx
var Input = forwardRef8(function Input2({
  asChild,
  children,
  startEnhancer,
  endEnhancer,
  onKeyDown,
  clearable,
  clearOnEscape,
  className,
  style,
  inputRef,
  disabled,
  onClick,
  onMouseDown,
  onEnter,
  value,
  error,
  overrides,
  ...inputProps
}, ref) {
  var _a, _b, _c;
  const localInputRef = useRef(null);
  const clearValue = () => {
    var _a2;
    const input = localInputRef.current;
    if (input) {
      const nativeInputValueSetter = (_a2 = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value"
      )) == null ? void 0 : _a2.set;
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, "");
        input.dispatchEvent(
          new Event("input", { bubbles: true, cancelable: true })
        );
      }
    }
  };
  const handleClick = (event) => {
    var _a2;
    (_a2 = localInputRef.current) == null ? void 0 : _a2.focus();
    onClick == null ? void 0 : onClick(event);
  };
  const inputRefs = useMemo(
    () => inputRef ? [localInputRef, inputRef] : [localInputRef],
    [inputRef]
  );
  const Comp = asChild ? Slot : "input";
  return /* @__PURE__ */ React9.createElement(
    Flex,
    {
      className: classNames(inputVariants({ disabled, error }), className),
      ref,
      style,
      onClick: handleClick,
      onMouseDown,
      ...overrides == null ? void 0 : overrides.Container
    },
    startEnhancer,
    /* @__PURE__ */ React9.createElement(
      Comp,
      {
        "aria-invalid": error ? "true" : ((_a = overrides == null ? void 0 : overrides.Container) == null ? void 0 : _a["aria-controls"]) ? void 0 : "false",
        disabled,
        ref: useMergedRef(...inputRefs),
        value,
        onKeyDown: (event) => {
          var _a2;
          onKeyDown == null ? void 0 : onKeyDown(event);
          if (event.key === "Escape") {
            (_a2 = localInputRef.current) == null ? void 0 : _a2.blur();
            if (clearOnEscape) {
              clearValue();
            }
          }
          if (event.key === "Enter") {
            onEnter == null ? void 0 : onEnter(value != null ? value : event.currentTarget.value);
          }
        },
        ...inputProps,
        ...overrides == null ? void 0 : overrides.Input,
        className: classNames(
          "w-full border-0 bg-transparent outline-none placeholder:text-secondary",
          // Hides the "spin"/number picker buttons
          "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          preventInputZoomStyles,
          (_b = overrides == null ? void 0 : overrides.Input) == null ? void 0 : _b.className
        )
      },
      children
    ),
    clearable && (((_c = localInputRef.current) == null ? void 0 : _c.value) || value) && /* @__PURE__ */ React9.createElement(VerticalAligned, null, /* @__PURE__ */ React9.createElement(UnstyledButton, { onClick: clearValue }, /* @__PURE__ */ React9.createElement(
      Close,
      {
        fill: "interactive-icon-secondary-styles",
        size: 20,
        title: "Clear"
      }
    ))),
    endEnhancer
  );
});

// src/components/AuthCode/AuthCode.react.tsx
var DEFAULT_LENGTH = 6;
var EMPTY_VALUE = "";
var isNumber = (value) => {
  return !!value.match(/\d+/);
};
var isValidAuthCode = (code, expectedLength) => {
  return code.length === expectedLength && code.split("").every((element) => isNumber(element));
};
var AuthCode = forwardRef9(
  function AuthCode2({
    autoFocus,
    className,
    disabled,
    error,
    length = DEFAULT_LENGTH,
    overrides,
    style,
    onComplete
  }, ref) {
    const [code, setCode] = useState(Array(length).fill(EMPTY_VALUE));
    const inputsRef = useRef2([]);
    const updateCodeValue = (index, value) => {
      const newCode = [...code];
      newCode[index] = value;
      for (let i = index + 1; i < newCode.length; i++) {
        newCode[i] = EMPTY_VALUE;
      }
      setCode(newCode);
      const newCodeString = newCode.join("");
      if (isValidAuthCode(newCodeString, length)) {
        onComplete(newCodeString);
      }
    };
    const focusNext = (currentIndex) => {
      const nextInput = inputsRef.current[currentIndex + 1];
      nextInput == null ? void 0 : nextInput.focus();
      if (!nextInput) {
        const lastInput = inputsRef.current[currentIndex];
        lastInput == null ? void 0 : lastInput.blur();
      }
    };
    const pasteCode = (code2) => {
      const sanitizedCode = code2.trim().split("").filter(isNumber);
      const truncatedCode = Array(length).fill(EMPTY_VALUE);
      for (let i = 0; i < truncatedCode.length; i++) {
        truncatedCode[i] = sanitizedCode[i] || EMPTY_VALUE;
      }
      setCode(truncatedCode);
      if (isValidAuthCode(truncatedCode.join(""), length)) {
        inputsRef.current.forEach((input) => input.blur());
        onComplete(truncatedCode.join(""));
      }
    };
    const handlePaste = (event) => {
      const pastedCode = event.clipboardData.getData("Text");
      pasteCode(pastedCode);
      event.preventDefault();
    };
    const handleChange = (event, index) => {
      const { value } = event.target;
      if (value.length === 1 && isNumber(value)) {
        updateCodeValue(index, value);
        focusNext(index);
      }
    };
    const handleInput = (event) => {
      const { value } = event.target;
      if (value && value.length > 1 && isNumber(value)) {
        pasteCode(value);
      }
    };
    const handleKeyDown = (event, index) => {
      const { key } = event;
      const hasValue = !!code[index];
      const focusPrevious = (currentIndex) => {
        const previousInput = inputsRef.current[currentIndex - 1];
        if (previousInput) {
          event.preventDefault();
          previousInput.focus();
        }
      };
      switch (key) {
        case "Backspace": {
          if (hasValue) {
            updateCodeValue(index, EMPTY_VALUE);
          }
          if (index > 0) {
            updateCodeValue(index - 1, EMPTY_VALUE);
          }
          focusPrevious(index);
          break;
        }
        case "ArrowLeft": {
          focusPrevious(index);
          break;
        }
        case "ArrowRight": {
          event.preventDefault();
          focusNext(index);
          break;
        }
        default: {
          const isReplacingValue = isNumber(key) && hasValue;
          if (isReplacingValue) {
            event.preventDefault();
            updateCodeValue(index, key);
            focusNext(index);
          }
        }
      }
    };
    return /* @__PURE__ */ React10.createElement(
      InlineFlex,
      {
        "aria-disabled": disabled,
        className: classNames("gap-2", className),
        ref,
        style,
        ...overrides == null ? void 0 : overrides.Container
      },
      code.map((value, index) => {
        var _a;
        return /* @__PURE__ */ React10.createElement(
          Input,
          {
            "aria-label": `Digit ${index + 1}`,
            autoComplete: index === 0 ? "one-time-code" : "off",
            autoFocus: index === 0 && autoFocus,
            className: classNames(
              // !size-10 to override default w-full on input.
              "flex !size-10 flex-none items-center justify-center rounded-lg p-0",
              (_a = overrides == null ? void 0 : overrides.Inputs) == null ? void 0 : _a.className
            ),
            "data-testid": `input-${index}`,
            disabled,
            error,
            inputMode: "numeric",
            inputRef: (element) => {
              inputsRef.current[index] = element;
            },
            key: index,
            overrides: {
              Input: {
                style: {
                  textAlign: "center"
                }
              }
            },
            type: "text",
            value,
            onChange: (event) => handleChange(event, index),
            onInput: handleInput,
            onKeyDown: (event) => handleKeyDown(event, index),
            onPaste: handlePaste
          }
        );
      })
    );
  }
);

// src/components/Carousel/Carousel.react.tsx
import AutoHeightPlugin from "embla-carousel-auto-height";
import AutoplayPlugin from "embla-carousel-autoplay";
import ClassNamesPlugin2 from "embla-carousel-class-names";
import useEmblaCarousel2 from "embla-carousel-react";
import React16, {
  forwardRef as forwardRef13,
  useCallback as useCallback2,
  useEffect as useEffect2,
  useState as useState2,
  useMemo as useMemo3
} from "react";
import { useContextSelector as useContextSelector4 } from "use-context-selector";

// src/components/Carousel/CarouselNavigationArrows.react.tsx
import React13, { forwardRef as forwardRef10 } from "react";
import { useContextSelector } from "use-context-selector";

// src/icons/material-symbols/outlined/ChevronLeft.react.tsx
import React11 from "react";
var ChevronLeft = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React11.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React11.createElement("title", null, "Chevron Left"),
    /* @__PURE__ */ React11.createElement("path", { d: "M561-240 320-481l241-241 43 43-198 198 198 198-43 43Z" })
  );
};

// src/icons/material-symbols/outlined/ChevronRight.react.tsx
import React12 from "react";
var ChevronRight = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React12.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React12.createElement("title", null, "Chevron Right"),
    /* @__PURE__ */ React12.createElement("path", { d: "M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z" })
  );
};

// src/components/Carousel/CarouselProvider.react.tsx
import { createContext } from "use-context-selector";
var CarouselContext = createContext(null);

// src/components/Carousel/CarouselNavigationArrows.react.tsx
var CarouselArrowNavButton = forwardRef10(function CarouselArrowNavButton2({ className, children, disabled, ...props }, ref) {
  return /* @__PURE__ */ React13.createElement(
    UnstyledButton,
    {
      className: classNames(
        "absolute top-1/2 flex w-12 -translate-y-1/2 items-center justify-center",
        { hidden: disabled },
        className
      ),
      ref,
      ...props
    },
    children
  );
});
var CarouselPrevious = forwardRef10(
  function CarouselPrevious2({ className, ...props }, ref) {
    const [scrollPrev, canScrollPrev] = useContextSelector(
      CarouselContext,
      (ctx) => [ctx == null ? void 0 : ctx.scrollPrev, ctx == null ? void 0 : ctx.canScrollPrev]
    );
    return /* @__PURE__ */ React13.createElement(
      CarouselArrowNavButton,
      {
        "aria-label": "Previous",
        className: classNames(
          "left-0 opacity-0 transition-opacity duration-200 group-hover/carousel:opacity-100",
          className
        ),
        disabled: !canScrollPrev,
        ref,
        onClick: scrollPrev,
        ...props
      },
      /* @__PURE__ */ React13.createElement(ChevronLeft, { size: 32 })
    );
  }
);
var CarouselNext = forwardRef10(
  function CarouselNext2({ className, ...props }, ref) {
    const [scrollNext, canScrollNext] = useContextSelector(
      CarouselContext,
      (ctx) => [ctx == null ? void 0 : ctx.scrollNext, ctx == null ? void 0 : ctx.canScrollNext]
    );
    return /* @__PURE__ */ React13.createElement(
      CarouselArrowNavButton,
      {
        "aria-label": "Next",
        className: classNames(
          "right-0 opacity-0 transition-opacity duration-200 group-hover/carousel:opacity-100",
          className
        ),
        disabled: !canScrollNext,
        ref,
        onClick: scrollNext,
        ...props
      },
      /* @__PURE__ */ React13.createElement(ChevronRight, { size: 32 })
    );
  }
);
var CarouselNavigationArrows = Object.assign(
  {},
  {
    Previous: CarouselPrevious,
    Next: CarouselNext
  }
);

// src/components/Carousel/CarouselNavigationDots.react.tsx
import React14, { forwardRef as forwardRef11 } from "react";
import { useContextSelector as useContextSelector2 } from "use-context-selector";
var CarouselNavigationDots = forwardRef11(function CarouselNavigationDots2({ className, dotClassName, ...props }, ref) {
  const [api, selectedIndex] = useContextSelector2(CarouselContext, (ctx) => [
    ctx == null ? void 0 : ctx.api,
    ctx == null ? void 0 : ctx.selectedIndex
  ]);
  const slideCount = api == null ? void 0 : api.slideNodes().length;
  if (!slideCount) {
    return null;
  }
  return /* @__PURE__ */ React14.createElement(
    "div",
    {
      className: classNames(
        "flex items-center justify-center gap-3",
        className
      ),
      ref,
      ...props
    },
    Array.from({ length: slideCount }).map((_, index) => /* @__PURE__ */ React14.createElement(
      UnstyledButton,
      {
        className: classNames(
          "h-1.5 w-10 rounded bg-text-primary opacity-20 transition-opacity duration-200",
          {
            "opacity-100": index === selectedIndex
          },
          dotClassName
        ),
        key: index,
        onClick: () => api == null ? void 0 : api.scrollTo(index)
      }
    ))
  );
});

// src/components/Carousel/CarouselNavigationThumbs.react.tsx
import ClassNamesPlugin from "embla-carousel-class-names";
import useEmblaCarousel from "embla-carousel-react";
import React15, { forwardRef as forwardRef12, useCallback, useEffect, useMemo as useMemo2 } from "react";
import { createContext as createContext2, useContextSelector as useContextSelector3 } from "use-context-selector";
var CarouselThumbsContext = createContext2(
  null
);
var CarouselThumbsBase = forwardRef12(
  function CarouselThumbContainer({ children, className, jumpOnClick, ...props }, ref) {
    const [scrollMainCarousel, navigationApi, setNavigationApi] = useContextSelector3(CarouselContext, (ctx) => [
      ctx == null ? void 0 : ctx.scrollTo,
      ctx == null ? void 0 : ctx.navigationApi,
      ctx == null ? void 0 : ctx.setNavigationApi
    ]);
    const [thumbsCarouselRef, thumbsCarouselApi] = useEmblaCarousel(
      {
        containScroll: "keepSnaps"
      },
      [ClassNamesPlugin()]
    );
    useEffect(() => {
      if (!thumbsCarouselApi || navigationApi) {
        return;
      }
      setNavigationApi == null ? void 0 : setNavigationApi(thumbsCarouselApi);
      return () => {
        setNavigationApi == null ? void 0 : setNavigationApi(void 0);
      };
    }, [thumbsCarouselApi]);
    const onThumbClick = useCallback(
      (index) => {
        scrollMainCarousel == null ? void 0 : scrollMainCarousel(index, jumpOnClick);
      },
      [scrollMainCarousel, jumpOnClick]
    );
    const value = useMemo2(
      () => ({
        thumbsApi: thumbsCarouselApi,
        onThumbClick
      }),
      [thumbsCarouselApi, onThumbClick]
    );
    return /* @__PURE__ */ React15.createElement(CarouselThumbsContext.Provider, { value }, /* @__PURE__ */ React15.createElement(
      "div",
      {
        "aria-roledescription": "thumbnail carousel",
        className: classNames("relative", className),
        ref,
        role: "region",
        ...props
      },
      /* @__PURE__ */ React15.createElement(
        "div",
        {
          className: classNames("overflow-hidden"),
          ref: thumbsCarouselRef
        },
        /* @__PURE__ */ React15.createElement("div", { className: classNames("flex [&>button:last-child]:!mr-0") }, children)
      )
    ));
  }
);
var CarouselThumbsItem = forwardRef12(function CarouselThumbsItem2({ children, className, index }, ref) {
  const [selectedIndex] = useContextSelector3(CarouselContext, (ctx) => [
    ctx == null ? void 0 : ctx.selectedIndex
  ]);
  const isSelected = selectedIndex === index;
  const onThumbClick = useContextSelector3(
    CarouselThumbsContext,
    (ctx) => ctx == null ? void 0 : ctx.onThumbClick
  );
  return /* @__PURE__ */ React15.createElement(
    UnstyledButton,
    {
      className: classNames("shrink-0 grow-0", className),
      ref,
      onClick: () => onThumbClick == null ? void 0 : onThumbClick(index)
    },
    typeof children === "function" ? children({ isSelected }) : children
  );
});
var CarouselThumbs = Object.assign(CarouselThumbsBase, {
  Item: CarouselThumbsItem
});

// src/components/Carousel/CarouselNavigation.react.tsx
var CarouselNavigation = Object.assign(
  {},
  {
    Arrows: CarouselNavigationArrows,
    Thumbs: CarouselThumbs,
    Dots: CarouselNavigationDots
  }
);

// src/components/Carousel/Carousel.react.tsx
var BaseCarousel = forwardRef13(function Carousel({
  animateHeight,
  autoplay,
  autoplayOptions,
  autoHeight,
  children,
  className,
  options,
  setApi,
  ...props
}, ref) {
  const [carouselRef, api] = useEmblaCarousel2(
    { inViewThreshold: 0.9, ...options },
    [
      ClassNamesPlugin2(),
      autoplay ? AutoplayPlugin({
        rootNode: (carouselRoot) => carouselRoot,
        ...autoplayOptions
      }) : null,
      autoHeight ? AutoHeightPlugin() : null
    ].filter(Boolean)
  );
  const [canScrollPrev, setCanScrollPrev] = useState2(false);
  const [canScrollNext, setCanScrollNext] = useState2(false);
  const [selectedIndex, setSelectedIndex] = useState2(0);
  const [navigationApi, setNavigationApi] = useState2();
  const onSelect = useCallback2(
    (api2) => {
      if (!api2) {
        return;
      }
      if (navigationApi) {
        navigationApi.scrollTo(api2.selectedScrollSnap());
      }
      setSelectedIndex(api2.selectedScrollSnap());
      setCanScrollPrev(api2.canScrollPrev());
      setCanScrollNext(api2.canScrollNext());
    },
    [navigationApi]
  );
  const scrollPrev = useCallback2(() => {
    api == null ? void 0 : api.scrollPrev();
  }, [api]);
  const scrollNext = useCallback2(() => {
    api == null ? void 0 : api.scrollNext();
  }, [api]);
  const scrollTo = useCallback2(
    (...options2) => {
      api == null ? void 0 : api.scrollTo(...options2);
    },
    [api]
  );
  const handleKeyDown = useCallback2(
    (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );
  useEffect2(() => {
    if (!api || !setApi) {
      return;
    }
    setApi(api);
  }, [api, setApi]);
  useEffect2(() => {
    if (!api) {
      return;
    }
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    return () => {
      api == null ? void 0 : api.off("select", onSelect);
      api == null ? void 0 : api.off("reInit", onSelect);
    };
  }, [api, onSelect]);
  const value = useMemo3(
    () => ({
      carouselRef,
      api,
      autoHeight,
      animateHeight,
      options,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
      navigationApi,
      setNavigationApi,
      scrollTo,
      selectedIndex
    }),
    [
      carouselRef,
      api,
      autoHeight,
      animateHeight,
      options,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
      navigationApi,
      setNavigationApi,
      scrollTo,
      selectedIndex
    ]
  );
  return /* @__PURE__ */ React16.createElement(CarouselContext.Provider, { value }, /* @__PURE__ */ React16.createElement(
    "div",
    {
      "aria-roledescription": "carousel",
      className: classNames("group/carousel relative", className),
      ref,
      role: "region",
      onKeyDownCapture: handleKeyDown,
      ...props
    },
    children
  ));
});
var CarouselContent = forwardRef13(function CarouselContent2({ className, ...props }, ref) {
  const [autoHeight, animateHeight, carouselRef, loop] = useContextSelector4(
    CarouselContext,
    (ctx) => {
      var _a;
      return [
        ctx == null ? void 0 : ctx.autoHeight,
        ctx == null ? void 0 : ctx.animateHeight,
        ctx == null ? void 0 : ctx.carouselRef,
        (_a = ctx == null ? void 0 : ctx.options) == null ? void 0 : _a.loop
      ];
    }
  );
  return /* @__PURE__ */ React16.createElement("div", { className: "overflow-hidden", ref: carouselRef }, /* @__PURE__ */ React16.createElement(
    "div",
    {
      className: classNames(
        "flex",
        {
          // If the carousel is not set to loop, remove the margin on the last child
          "[&>div:last-child]:!mr-0": !loop,
          "items-start": autoHeight,
          "transition-height duration-200": animateHeight
        },
        className
      ),
      ref,
      ...props
    }
  ));
});
var CarouselItem = forwardRef13(function CarouselSlide({ className, ...props }, ref) {
  return /* @__PURE__ */ React16.createElement(
    "div",
    {
      "aria-roledescription": "slide",
      className: classNames(
        "min-w-0 shrink-0 grow-0 basis-full pl-4",
        className
      ),
      ...props,
      ref,
      role: "group"
    }
  );
});
var Carousel2 = Object.assign(BaseCarousel, {
  Content: CarouselContent,
  Item: CarouselItem,
  Nav: CarouselNavigation
});

// src/components/Collapsible/Collapsible.react.tsx
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import React17, { forwardRef as forwardRef14 } from "react";
var Collapsible = forwardRef14(function Collapsible2({ children, content, ...rest }, ref) {
  return /* @__PURE__ */ React17.createElement(CollapsiblePrimitive.Root, { ...rest, ref }, /* @__PURE__ */ React17.createElement(CollapsiblePrimitive.CollapsibleTrigger, { asChild: true }, children), /* @__PURE__ */ React17.createElement(CollapsiblePrimitive.CollapsibleContent, { asChild: true }, content));
});

// src/components/Combobox/Combobox.react.tsx
import useMergedRef2 from "@react-hook/merged-ref";
import React27, {
  forwardRef as forwardRef23,
  useCallback as useCallback3,
  useRef as useRef4,
  useState as useState4
} from "react";

// src/components/Command/Command.react.tsx
import { Command as CommandPrimitive } from "cmdk";
import React24, {
  createContext as createContext3,
  forwardRef as forwardRef20,
  useContext,
  useMemo as useMemo4,
  useState as useState3
} from "react";

// src/hooks/useIsMounted.ts
import { useEffect as useEffect3, useRef as useRef3 } from "react";
var useIsMountedRef = () => {
  const isMounted = useRef3(false);
  useEffect3(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return isMounted;
};

// src/icons/material-symbols/outlined/Search.react.tsx
import React18 from "react";
var Search = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React18.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "imt",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React18.createElement("title", null, "Search"),
    /* @__PURE__ */ React18.createElement("path", { d: "M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" })
  );
};

// src/components/CenterAligned/CenterAligned.react.tsx
import React19, { forwardRef as forwardRef15 } from "react";
var CenterAligned = forwardRef15(
  function CenterAligned2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React19.createElement(
      VerticalAligned,
      {
        className: classNames("items-center", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/Item/Item.react.tsx
import React23, { forwardRef as forwardRef19 } from "react";
import { tv as tv9 } from "tailwind-variants";

// src/utils/element.ts
var isClickableElement = (props) => {
  return Boolean(props.onClick);
};
var isLinkElement = (props) => {
  return Boolean(props.href);
};
var isInteractiveElement = (props) => {
  return isLinkElement(props) || isClickableElement(props);
};

// src/components/Avatar/Avatar.react.tsx
import React20, {
  forwardRef as forwardRef16
} from "react";
var isImage = (props) => {
  return props.src !== void 0;
};
var isIcon = (props) => {
  return props.icon !== void 0;
};
var isVideo = (props) => {
  return [".webm", ".mp4", ".m4v", ".ogg", ".ogv", ".mov"].some(
    (ext) => {
      var _a;
      return (_a = props.src) == null ? void 0 : _a.toLowerCase().endsWith(ext);
    }
  );
};
var ImgAvatar = forwardRef16(
  function ImgAvatar2({ renderImage, ...rest }, ref) {
    const Comp = renderImage != null ? renderImage : "img";
    return /* @__PURE__ */ React20.createElement(Comp, { ...rest, ref });
  }
);
var VideoAvatar = forwardRef16(
  function VideoAvatar2(props, ref) {
    return /* @__PURE__ */ React20.createElement("video", { ...props, ref });
  }
);
var AvatarDataId = "Avatar";
var Avatar = Object.assign(
  forwardRef16(
    function Avatar2({
      className: propsClassName,
      renderImage,
      rounded,
      size = 24,
      style,
      ...props
    }, ref) {
      const className = classNames(
        "overflow-hidden",
        rounded && "rounded-full",
        propsClassName
      );
      const sizeStyles = {
        width: `${size}px`,
        height: `${size}px`,
        ...style
      };
      if (isVideo(props)) {
        return /* @__PURE__ */ React20.createElement(
          VideoAvatar,
          {
            className,
            "data-id": AvatarDataId,
            ref,
            size,
            style: sizeStyles,
            ...props
          }
        );
      } else if (isImage(props)) {
        const { objectFit, ...rest } = props;
        return /* @__PURE__ */ React20.createElement(
          ImgAvatar,
          {
            className: classNames(objectFitVariants({ objectFit }), className),
            "data-id": AvatarDataId,
            ref,
            renderImage,
            size,
            style: sizeStyles,
            ...rest
          }
        );
      } else if (isIcon(props)) {
        const { icon: IconComponent } = props;
        return /* @__PURE__ */ React20.createElement(
          IconComponent,
          {
            className,
            "data-id": AvatarDataId,
            size,
            title: props.title
          }
        );
      } else {
        return /* @__PURE__ */ React20.createElement(
          CenterAligned,
          {
            className,
            "data-id": AvatarDataId,
            ref,
            style: sizeStyles,
            ...props
          }
        );
      }
    }
  ),
  { dataId: AvatarDataId }
);

// src/components/Link/Link.react.tsx
import { sanitizeUrl } from "@braintree/sanitize-url";
import React21, { forwardRef as forwardRef17 } from "react";
var Link = forwardRef17(function Link2({ className, href, variant = "default", disabled, rel, target, ...rest }, ref) {
  if (href && !href.startsWith("/")) {
    target = target != null ? target : "_blank";
    rel = rel != null ? rel : "nofollow noopener";
  }
  return /* @__PURE__ */ React21.createElement(
    "a",
    {
      "aria-disabled": disabled != null ? disabled : void 0,
      className: classNames(
        linkVariants({ variant }),
        disabledVariants({ disabled }),
        className
      ),
      href: href ? sanitizeUrl(href) : void 0,
      ref,
      rel,
      tabIndex: disabled ? -1 : void 0,
      target,
      ...rest
    }
  );
});

// src/components/Text/Text.react.tsx
import React22, { forwardRef as forwardRef18 } from "react";
import { tv as tv8 } from "tailwind-variants";
var textBodyVariants = tv8({
  base: "leading-normal",
  variants: {
    size: {
      tiny: "text-xs",
      small: "text-sm",
      medium: "text-md"
    },
    weight: {
      regular: "font-normal",
      semibold: "font-semibold"
    },
    responsive: {
      true: ""
    }
  },
  compoundVariants: [
    {
      size: "small",
      responsive: true,
      className: "text-xs lg:text-sm"
    },
    {
      size: "medium",
      responsive: true,
      className: "text-sm lg:text-md"
    }
  ]
});
var BodyDataId = "TextBody";
var TextBody = Object.assign(
  forwardRef18(function TextBody2({
    asChild,
    className,
    weight,
    responsive = true,
    color,
    size = "medium",
    ...rest
  }, ref) {
    const Comp = asChild ? Slot : "span";
    return /* @__PURE__ */ React22.createElement(
      Comp,
      {
        className: classNames(
          textBodyVariants({
            weight,
            size,
            responsive
          }),
          textColorVariants({ color }),
          className
        ),
        "data-id": BodyDataId,
        ref,
        ...rest
      }
    );
  }),
  {
    dataId: BodyDataId
  }
);
var headingVariants = tv8({
  variants: {
    size: {
      tiny: "text-heading-xs",
      small: "text-heading-sm",
      medium: "text-heading-md",
      large: "text-heading-lg"
    },
    responsive: {
      true: ""
    }
  },
  compoundVariants: [
    {
      size: "small",
      responsive: true,
      className: "text-heading-xs lg:text-heading-sm"
    },
    {
      size: "medium",
      responsive: true,
      className: "text-heading-xs lg:text-heading-sm 2xl:text-heading-md"
    },
    {
      size: "large",
      responsive: true,
      className: "text-heading-sm md:text-heading-md lg:text-heading-lg"
    }
  ]
});
var HeadingDataId = "TextHeading";
var TextHeading = Object.assign(
  forwardRef18(function TextHeading2({ asChild, className, responsive = true, color, size, ...rest }, ref) {
    const Comp = asChild ? Slot : "span";
    return /* @__PURE__ */ React22.createElement(
      Comp,
      {
        className: classNames(
          "font-semibold",
          headingVariants({ size, responsive, className: "leading-none" }),
          textColorVariants({ color }),
          className
        ),
        "data-id": HeadingDataId,
        ref,
        ...rest
      }
    );
  }),
  { dataId: HeadingDataId }
);
var displayVariants = tv8({
  variants: {
    size: {
      small: "text-display-sm",
      medium: "text-display-md",
      large: "text-display-lg",
      xlarge: "text-display-xl"
    },
    responsive: {
      true: ""
    }
  },
  compoundVariants: [
    {
      size: "medium",
      responsive: true,
      className: "text-display-sm md:text-display-md"
    },
    {
      size: "large",
      responsive: true,
      className: "text-display-sm md:text-display-md xl:text-display-lg"
    },
    {
      size: "xlarge",
      responsive: true,
      className: "text-display-md md:text-display-xl"
    }
  ]
});
var DisplayDataId = "TextDisplay";
var TextDisplay = Object.assign(
  forwardRef18(function TextDisplay2({ asChild, className, color, responsive = true, size, ...rest }, ref) {
    const Comp = asChild ? Slot : "span";
    return /* @__PURE__ */ React22.createElement(
      Comp,
      {
        className: classNames(
          "font-semibold",
          displayVariants({ size, responsive, className: "leading-none" }),
          textColorVariants({ color }),
          className
        ),
        "data-id": DisplayDataId,
        ref,
        ...rest
      }
    );
  }),
  { dataId: DisplayDataId }
);
var Text = Object.assign(TextBody, {
  Body: TextBody,
  Heading: TextHeading,
  Display: TextDisplay
});

// src/components/Item/Item.react.tsx
var itemVariants = tv9({
  variants: {
    active: {
      true: "bg-component-gray-2"
    },
    interactive: {
      true: "cursor-pointer hover:bg-component-gray-1 active:bg-component-gray-2"
    },
    variant: {
      unstyled: "border-0 p-0"
    }
  }
});
var LinkItem = forwardRef19(function LinkItem2({ renderLink, ...rest }, ref) {
  const Comp = renderLink != null ? renderLink : Link;
  return /* @__PURE__ */ React23.createElement(Comp, { ref, variant: "unstyled", ...rest });
});
var ItemDataId = "Item";
var ItemBase = Object.assign(
  forwardRef19(function Item({ asChild, children, variant, interactive, renderLink, ...props }, ref) {
    const isInteractive = interactive != null ? interactive : isInteractiveElement(props);
    const { className, disabled, href, target, onClick, ...rest } = props;
    const contentProps = { href, target, onClick, disabled };
    const renderContent = () => {
      if (asChild) {
        return children;
      }
      if (isLinkElement(contentProps)) {
        return /* @__PURE__ */ React23.createElement(
          LinkItem,
          {
            disabled: contentProps.disabled,
            href: contentProps.href,
            renderLink,
            target: contentProps.target
          },
          children
        );
      }
      if (isClickableElement(contentProps)) {
        return /* @__PURE__ */ React23.createElement(UnstyledButton, { disabled: contentProps.disabled }, children);
      }
      return /* @__PURE__ */ React23.createElement("div", null, children);
    };
    const isUnstyled = variant === "unstyled";
    return /* @__PURE__ */ React23.createElement(
      Slot,
      {
        className: classNames(
          "flex w-full border border-level-2 p-4",
          itemVariants({
            active: !isUnstyled && rest.active,
            interactive: !isUnstyled && !disabled && isInteractive,
            variant
          }),
          className
        ),
        "data-id": ItemDataId,
        ref,
        onClick,
        ...rest
      },
      renderContent()
    );
  }),
  { dataId: ItemDataId }
);
var ItemAvatarDataId = "ItemAvatar";
var ItemAvatar = Object.assign(
  forwardRef19(function ItemAvatar2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React23.createElement(
      Avatar,
      {
        className: classNames(
          "order-2 mr-4 shrink-0 self-center text-primary",
          className
        ),
        "data-id": ItemAvatarDataId,
        ref,
        ...rest
      }
    );
  }),
  { dataId: ItemAvatarDataId }
);
var ItemContentDataId = "ItemContent";
var ItemContent = ({ className, ...rest }) => {
  return /* @__PURE__ */ React23.createElement(
    VerticalAligned,
    {
      className: classNames(
        "order-3 mr-4 overflow-hidden",
        "flex-auto items-start self-stretch",
        className
      ),
      "data-id": ItemContentDataId,
      ...rest
    }
  );
};
ItemContent.dataId = ItemContentDataId;
var ItemTitleDataId = "ItemTitle";
var ItemTitle = Object.assign(
  forwardRef19(function ItemTitle2({ color = "primary", weight = "semibold", ...rest }, ref) {
    return /* @__PURE__ */ React23.createElement(
      Text,
      {
        color,
        "data-id": ItemTitleDataId,
        weight,
        ...rest,
        ref
      }
    );
  }),
  { dataId: ItemTitleDataId }
);
var ItemDescription = forwardRef19(
  function ItemDescription2({ color = "secondary", size = "small", ...rest }, ref) {
    return /* @__PURE__ */ React23.createElement(Text, { color, size, ...rest, ref });
  }
);
var ItemSide = ({ className, ...rest }) => {
  return /* @__PURE__ */ React23.createElement(
    FlexColumn,
    {
      className: classNames(
        "order-4 max-w-[40%] overflow-hidden text-right",
        "flex-[0_0_auto] justify-center self-stretch ",
        className
      ),
      ...rest
    }
  );
};
var ItemAction = ({ className, ...rest }) => {
  return /* @__PURE__ */ React23.createElement(
    FlexColumn,
    {
      className: classNames("order-5 ml-3 justify-center", className),
      ...rest
    }
  );
};
var Item2 = Object.assign(ItemBase, {
  Avatar: ItemAvatar,
  Content: ItemContent,
  Title: ItemTitle,
  Description: ItemDescription,
  Side: ItemSide,
  Action: ItemAction
});

// src/components/Command/Command.react.tsx
var CommandContext = createContext3({ setLocalValue: (_value) => {
} });
var CommandBase = forwardRef20(function Command({ className, ...props }, ref) {
  const [localValue, setLocalValue] = useState3("");
  const isMountedRef = useIsMountedRef();
  const value = useMemo4(() => ({ setLocalValue }), [setLocalValue]);
  return /* @__PURE__ */ React24.createElement(CommandContext.Provider, { value }, /* @__PURE__ */ React24.createElement(
    CommandPrimitive,
    {
      className: classNames(
        "flex size-full flex-col gap-3 overflow-hidden",
        className
      ),
      ref,
      value: localValue,
      onValueChange: (e) => {
        if (isMountedRef.current) {
          setLocalValue(e);
        }
      },
      ...props
    }
  ));
});
var CommandInput = forwardRef20(function CommandInput2({ className, ...props }, ref) {
  return /* @__PURE__ */ React24.createElement(
    Input,
    {
      asChild: true,
      startEnhancer: /* @__PURE__ */ React24.createElement(Search, { className: "mr-1", fill: "secondary" }),
      ...props
    },
    /* @__PURE__ */ React24.createElement(CommandPrimitive.Input, { className, ref })
  );
});
var CommandList = forwardRef20(function CommandList2({ className, ...props }, ref) {
  const { setLocalValue } = useContext(CommandContext);
  return /* @__PURE__ */ React24.createElement(
    CommandPrimitive.List,
    {
      className: classNames(
        "max-h-[300px] overflow-y-auto overflow-x-hidden",
        className
      ),
      ref,
      onMouseLeave: () => setLocalValue(""),
      ...props
    }
  );
});
var CommandEmpty = forwardRef20(function CommandEmpty2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React24.createElement(
    Text,
    {
      asChild: true,
      className: classNames("py-6 text-center", className),
      size: "small",
      weight: "semibold"
    },
    /* @__PURE__ */ React24.createElement(CommandPrimitive.Empty, { ref, ...rest })
  );
});
var CommandItemBase = forwardRef20(function CommandItem({ className, onSelect, ...props }, ref) {
  return /* @__PURE__ */ React24.createElement(
    Item2,
    {
      asChild: true,
      className: classNames(
        "rounded-xl border-0 focus:outline-none aria-selected:bg-component-gray-1",
        className
      ),
      interactive: Boolean(onSelect)
    },
    /* @__PURE__ */ React24.createElement(CommandPrimitive.Item, { ref, ...props, onSelect })
  );
});
var CommandItemAvatar = Item2.Avatar;
var CommandItemTitle = Item2.Title;
var CommandItemDescription = Item2.Description;
var CommandItemContent = Item2.Content;
var CommandItemSide = Item2.Side;
var CommandShortcut = forwardRef20(
  function CommandShortcut2({ children, ...rest }, ref) {
    return /* @__PURE__ */ React24.createElement(
      CenterAligned,
      {
        className: "size-8 rounded bg-component-gray-1 px-3.5 py-1.5",
        ref,
        ...rest
      },
      /* @__PURE__ */ React24.createElement(Text, { size: "small" }, children)
    );
  }
);
var CommandItem2 = Object.assign(CommandItemBase, {
  Avatar: CommandItemAvatar,
  Title: CommandItemTitle,
  Description: CommandItemDescription,
  Content: CommandItemContent,
  Side: CommandItemSide
});
var CommandGroup = forwardRef20(function CommandGroup2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React24.createElement(
    CommandPrimitive.Group,
    {
      className: classNames(
        "[&_[cmdk-group-heading]]:py-2",
        textBodyVariants({ size: "medium" }),
        className
      ),
      ref,
      ...rest
    }
  );
});
var Command2 = Object.assign(CommandBase, {
  Input: CommandInput,
  List: CommandList,
  Empty: CommandEmpty,
  Item: CommandItem2,
  Shortcut: CommandShortcut,
  Group: CommandGroup
});

// src/components/Popover/Popover.react.tsx
import * as PopoverPrimitive from "@radix-ui/react-popover";
import React25, { forwardRef as forwardRef21 } from "react";

// src/styles/utils/tooltip.ts
import { tv as tv10 } from "tailwind-variants";
var tooltipVariants = tv10({
  base: classNames(
    "z-tooltip px-4 py-3",
    "rounded text-sm font-semibold outline-none",
    // Handling both Tooltip and Popover states for convenience
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in",
    "data-[state=delayed-open]:animate-in data-[state=instant-open]:animate-in data-[state=delayed-open]:fade-in data-[state=instant-open]:fade-in"
  ),
  variants: {
    elevation: {
      "level-2": "bg-elevation-2 shadow-elevation-2",
      "level-3": "bg-elevation-3 shadow-elevation-3"
    },
    variant: {
      default: "",
      translucent: "bg-elevation-3/60 font-medium shadow-elevation-1 backdrop-blur-[20px]"
    },
    animation: {
      "fade-in": "",
      "slide-out": classNames(
        "data-[side=top]:slide-in-from-bottom-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2"
      )
    }
  }
});
var tooltipArrowVariants = tv10({
  variants: {
    elevation: {
      "level-2": "fill-bg-elevation-2",
      "level-3": "fill-bg-elevation-3"
    }
  }
});

// src/components/Popover/Popover.react.tsx
var Popover = forwardRef21(function Popover2({
  open,
  defaultOpen,
  onOpenChange,
  content,
  children,
  className,
  disabled,
  elevation = "level-3",
  align = "center",
  variant = "default",
  arrow = variant === "default",
  sideOffset = variant === "translucent" ? 8 : 4,
  animation = "fade-in",
  portal = true,
  ...props
}, ref) {
  const popoverContent = !disabled && /* @__PURE__ */ React25.createElement(
    PopoverPrimitive.Content,
    {
      align,
      className: classNames(
        "overflow-auto",
        "min-w-[var(--radix-popover-trigger-width)]",
        "max-h-[350px]",
        tooltipVariants({ elevation, variant, animation }),
        className
      ),
      ref,
      sideOffset,
      ...props
    },
    arrow && /* @__PURE__ */ React25.createElement(
      PopoverPrimitive.Arrow,
      {
        className: tooltipArrowVariants({ elevation })
      }
    ),
    content
  );
  return /* @__PURE__ */ React25.createElement(
    PopoverPrimitive.Root,
    {
      defaultOpen,
      open,
      onOpenChange
    },
    /* @__PURE__ */ React25.createElement(PopoverPrimitive.Trigger, { asChild: true }, children),
    portal ? /* @__PURE__ */ React25.createElement(PopoverPrimitive.Portal, null, popoverContent) : popoverContent
  );
});

// src/components/Separator/Separator.react.tsx
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import React26, { forwardRef as forwardRef22 } from "react";
var Separator = forwardRef22(function Separator2({ className, orientation = "horizontal", decorative = true }, ref) {
  return /* @__PURE__ */ React26.createElement(
    SeparatorPrimitive.Root,
    {
      className: classNames(
        "shrink-0 bg-border-1",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      decorative,
      orientation,
      ref
    }
  );
});

// src/components/Combobox/Combobox.react.tsx
var ComboboxBase = forwardRef23(
  function Combobox({
    open,
    onOpenChange,
    defaultOpen,
    content,
    filter,
    shouldFilter,
    label,
    onOpenAutoFocus,
    onFocus,
    onKeyDown,
    onInteractOutside,
    onEscapeKeyDown,
    overrides,
    ...rest
  }, ref) {
    const [openedState, setOpenedState] = useState4(defaultOpen != null ? defaultOpen : open);
    const resolvedOpenState = open === void 0 ? openedState : open;
    const inputRef = useRef4(null);
    const handleOnOpenChange = useCallback3((open2) => {
      setOpenedState(open2);
      onOpenChange == null ? void 0 : onOpenChange(open2);
    }, []);
    return /* @__PURE__ */ React27.createElement(Command2, { filter, label, shouldFilter }, /* @__PURE__ */ React27.createElement(
      Popover,
      {
        arrow: false,
        content,
        defaultOpen,
        open: resolvedOpenState,
        portal: false,
        sideOffset: 8,
        onEscapeKeyDown: (e) => {
          handleOnOpenChange(false);
          onEscapeKeyDown == null ? void 0 : onEscapeKeyDown(e);
        },
        onInteractOutside: (e) => {
          if (e.target === inputRef.current) {
            e.preventDefault();
            return;
          }
          handleOnOpenChange(false);
          onInteractOutside == null ? void 0 : onInteractOutside(e);
        },
        onOpenAutoFocus: (e) => {
          var _a;
          e.preventDefault();
          (_a = inputRef.current) == null ? void 0 : _a.focus();
          onOpenAutoFocus == null ? void 0 : onOpenAutoFocus(e);
        },
        ...overrides == null ? void 0 : overrides.Popover
      },
      /* @__PURE__ */ React27.createElement("div", { className: rest.className }, /* @__PURE__ */ React27.createElement(
        Command2.Input,
        {
          onFocus: (e) => {
            handleOnOpenChange(true);
            onFocus == null ? void 0 : onFocus(e);
          },
          onKeyDown: (e) => {
            if (e.key === "ArrowDown") {
              handleOnOpenChange(true);
            }
            onKeyDown == null ? void 0 : onKeyDown(e);
          },
          ...rest,
          ref: useMergedRef2(ref, inputRef)
        }
      ))
    ));
  }
);
var ComboboxList = CommandList;
var ComboboxEmpty = CommandEmpty;
var ComboboxItemBase = CommandItem2;
var ComboboxItemAvatar = CommandItemAvatar;
var ComboboxItemContent = CommandItemContent;
var ComboboxItemTitle = CommandItemTitle;
var ComboboxItemDescription = CommandItemDescription;
var ComboboxItemSide = CommandItemSide;
var ComboboxShortcut = CommandShortcut;
var ComboboxGroup = CommandGroup;
var ComboboxItem = Object.assign({}, ComboboxItemBase, {
  Avatar: ComboboxItemAvatar,
  Title: ComboboxItemTitle,
  Description: ComboboxItemDescription,
  Content: ComboboxItemContent,
  Side: ComboboxItemSide
});
var Combobox2 = Object.assign(ComboboxBase, {
  List: ComboboxList,
  Empty: ComboboxEmpty,
  Item: ComboboxItem,
  Shortcut: ComboboxShortcut,
  Separator,
  Group: ComboboxGroup
});

// src/components/Modal/Modal.react.tsx
import * as ModalPrimitive from "@radix-ui/react-dialog";
import React33, {
  forwardRef as forwardRef27,
  useCallback as useCallback4,
  useEffect as useEffect5,
  useRef as useRef5,
  useState as useState6
} from "react";
import { tv as tv14 } from "tailwind-variants";

// src/components/Button/Button.react.tsx
import React29, {
  forwardRef as forwardRef24,
  isValidElement
} from "react";
import { tv as tv12 } from "tailwind-variants";

// src/components/Spinner/Spinner.react.tsx
import React28 from "react";
import { tv as tv11 } from "tailwind-variants";
var spinnerVariants = tv11({
  variants: {
    size: {
      xsmall: "size-4 border-2",
      small: "size-6 border-[3px]",
      medium: "size-12 border-4",
      large: "size-[72px] border-[5px]"
    }
  }
});
var Spinner = ({
  className,
  color = "text-primary",
  size = "small"
}) => {
  return /* @__PURE__ */ React28.createElement(
    "span",
    {
      "aria-live": "polite",
      className: classNames(
        "inline-block animate-spin rounded-full",
        "!border-b-transparent",
        spinnerVariants({
          size
        }),
        // Default to small spinners on mobile only, except for when it's explicitly set to xsmall
        size !== "xsmall" && "xs-sm:size-6 xs-sm:border-[3px]",
        borderColorVariants({ color }),
        className
      ),
      role: "status"
    }
  );
};

// src/components/Button/Button.react.tsx
var buttonVariants = tv12({
  variants: {
    variant: {
      primary: "bg-blue-3 text-white hover:bg-blue-2",
      secondary: "bg-component-gray-2 text-primary hover:bg-component-gray-3",
      destructive: "bg-red-3/[0.08] text-red-3 hover:bg-red-3/[0.16]"
    },
    size: {
      small: "h-9 gap-2.5 rounded px-3 py-2.5",
      large: "h-10 gap-2 rounded px-4 py-3"
    },
    iconSide: {
      left: "",
      right: ""
    },
    onlyIcon: {
      true: ""
    }
  },
  compoundVariants: [
    {
      size: "large",
      iconSide: "left",
      className: "pl-3 pr-4"
    },
    {
      size: "large",
      iconSide: "right",
      className: "pl-4 pr-3"
    },
    {
      size: "small",
      onlyIcon: true,
      className: "px-2"
    },
    {
      size: "large",
      onlyIcon: true,
      className: "px-3"
    }
  ]
});
var hasCustomIcon = (props) => {
  return isValidElement(props.icon);
};
var hasAnyIcon = (props) => {
  return props.icon !== void 0;
};
var LinkButton = ({ renderLink, ...rest }) => {
  const Comp = renderLink != null ? renderLink : Link;
  return /* @__PURE__ */ React29.createElement(Comp, { ...rest, variant: "unstyled" });
};
var ButtonDataId = "Button";
var Button = Object.assign(
  forwardRef24(function Button2({
    renderLink,
    children,
    className,
    disabled = false,
    href,
    variant = "primary",
    size = "large",
    isLoading,
    type = "button",
    iconSide = "left",
    ...propsWithIcon
  }, ref) {
    const hasIcon = hasAnyIcon(propsWithIcon);
    const onlyIcon = hasIcon && !children;
    const renderContent = () => {
      if (isLoading) {
        let color;
        switch (variant) {
          case "primary":
            color = "white";
            break;
          case "destructive":
            color = "red-3";
            break;
        }
        return /* @__PURE__ */ React29.createElement(Spinner, { color });
      }
      const renderIcon = (buttonProps) => {
        var _a;
        const iconSize = size === "small" ? 20 : 24;
        return /* @__PURE__ */ React29.createElement(Flex, { "aria-hidden": "true", className: "pointer-events-none max-h-6" }, hasCustomIcon(buttonProps) ? buttonProps.icon : /* @__PURE__ */ React29.createElement(
          buttonProps.icon,
          {
            size: iconSize,
            ...(_a = buttonProps.overrides) == null ? void 0 : _a.Icon.props
          }
        ));
      };
      return /* @__PURE__ */ React29.createElement(React29.Fragment, null, hasIcon && iconSide === "left" && renderIcon(propsWithIcon), children, hasIcon && iconSide === "right" && renderIcon(propsWithIcon));
    };
    const { icon: _, ...props } = propsWithIcon;
    return /* @__PURE__ */ React29.createElement(
      Slot,
      {
        className: classNames(
          "inline-flex items-center justify-center whitespace-nowrap",
          "font-semibold transition duration-200",
          textBodyVariants({
            size: "small",
            weight: "semibold"
          }),
          buttonVariants({
            variant,
            size,
            iconSide: hasIcon && !onlyIcon ? iconSide : void 0,
            onlyIcon
          }),
          disabledVariants({ disabled }),
          className
        ),
        "data-id": ButtonDataId,
        ref,
        ...props
      },
      href ? /* @__PURE__ */ React29.createElement(LinkButton, { disabled, href, renderLink }, renderContent()) : /* @__PURE__ */ React29.createElement("button", { disabled, type }, renderContent())
    );
  }),
  { dataId: ButtonDataId }
);

// src/components/FlexCenter/FlexCenter.react.tsx
import React30, { forwardRef as forwardRef25 } from "react";
var FlexCenter = forwardRef25(
  function FlexCenter2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React30.createElement(
      Flex,
      {
        className: classNames("items-center", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/Media/createMedia.ts
import { createMedia as artsyCreateMedia } from "@artsy/fresnel";
import { useContextSelector as useContextSelector5 } from "use-context-selector";

// src/components/Media/createSizeProvider.tsx
import React31 from "react";
import { createContext as createContext4 } from "use-context-selector";

// src/hooks/useWindowSize.ts
import throttle from "lodash.throttle";
import { useEffect as useEffect4, useState as useState5 } from "react";
var useWindowSize = (initialWindowSize = {
  width: void 0,
  height: void 0
}) => {
  const [windowSize, setWindowSize] = useState5(initialWindowSize);
  useEffect4(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    const onResize = throttle(handleResize, 100);
    window.addEventListener("resize", onResize);
    handleResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return windowSize;
};

// src/components/Media/createSizeProvider.tsx
var createSizeProvider = ({
  initialWindowSize
}) => {
  const SizeContext = createContext4(
    initialWindowSize
  );
  const SizeProvider2 = ({ children }) => {
    const windowSize = useWindowSize(
      initialWindowSize
    );
    return /* @__PURE__ */ React31.createElement(SizeContext.Provider, { value: windowSize }, children);
  };
  return { SizeProvider: SizeProvider2, SizeContext };
};

// src/components/Media/createMedia.ts
function createMedia(breakpoints2, options = {}) {
  const { createMediaStyle: createMediaStyle2, Media: Media2, MediaContextProvider: MediaContextProvider2 } = artsyCreateMedia({
    breakpoints: breakpoints2
  });
  const { SizeProvider: SizeProvider2, SizeContext } = createSizeProvider(options);
  function useSizeContextSelector2(selector) {
    return useContextSelector5(
      SizeContext,
      selector
    );
  }
  return {
    createMediaStyle: createMediaStyle2,
    breakpoints: breakpoints2,
    SizeProvider: SizeProvider2,
    useSizeContextSelector: useSizeContextSelector2,
    Media: Media2,
    MediaContextProvider: MediaContextProvider2
  };
}

// src/components/Media/Media.react.ts
var {
  Media,
  SizeProvider,
  MediaContextProvider,
  useSizeContextSelector,
  createMediaStyle
} = createMedia(breakpoints);
var getBreakpointPixelValue = (bp) => {
  return breakpoints[bp];
};
var useLessThanBreakpointContextSelector = (breakpoint) => {
  return useSizeContextSelector(
    (value) => !!value.width && value.width < breakpoints[breakpoint]
  );
};
var useIsLessThanSm = () => useLessThanBreakpointContextSelector("sm");
var useIsLessThanMd = () => useLessThanBreakpointContextSelector("md");
var useIsLessThanLg = () => useLessThanBreakpointContextSelector("lg");
var useIsLessThanXl = () => useLessThanBreakpointContextSelector("xl");
var useIsLessThan2Xl = () => useLessThanBreakpointContextSelector("2xl");
var useIsLessThan3Xl = () => useLessThanBreakpointContextSelector("3xl");
var useIsLessThan4Xl = () => useLessThanBreakpointContextSelector("4xl");
var useIsLessThanMax = () => useLessThanBreakpointContextSelector("max");
var useIsGreaterThanOrEqualToLg = () => !useIsLessThanLg();
var useIsLessThanBreakpoint = (breakpoint) => {
  return useLessThanBreakpointContextSelector(breakpoint);
};
var mediaStyles = createMediaStyle();

// src/components/Sheet/Sheet.react.tsx
import * as SheetPrimitive from "@radix-ui/react-dialog";
import React32, {
  createContext as createContext5,
  forwardRef as forwardRef26,
  useContext as useContext2,
  useMemo as useMemo5
} from "react";
import { tv as tv13 } from "tailwind-variants";
var sheetVariants = tv13({
  base: "w-full border-level-2",
  variants: {
    side: {
      top: classNames(
        "inset-x-0 top-0 max-h-full",
        "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        "rounded-b-2xl border-b"
      ),
      bottom: classNames(
        "inset-x-0 bottom-0 max-h-full",
        "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        "rounded-t-2xl border-t"
      ),
      left: classNames(
        "inset-y-0 left-0 border-r lg:w-[420px]",
        "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left"
      ),
      right: classNames(
        "inset-y-0 right-0 border-l lg:w-[420px]",
        "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
      )
    }
  }
});
var SheetContext = createContext5({
  isVerticalSide: false
});
var SheetBase = forwardRef26(function Sheet({
  open,
  defaultOpen,
  onOpenChange,
  content,
  children,
  className,
  side = "right",
  withCloseIcon = true,
  overrides,
  modal,
  ...props
}, ref) {
  const value = useMemo5(
    () => ({
      isVerticalSide: side === "top" || side === "bottom"
    }),
    [side]
  );
  return /* @__PURE__ */ React32.createElement(SheetContext.Provider, { value }, /* @__PURE__ */ React32.createElement(
    SheetPrimitive.Root,
    {
      defaultOpen,
      modal,
      open,
      onOpenChange
    },
    /* @__PURE__ */ React32.createElement(SheetPrimitive.Trigger, { asChild: true }, children),
    /* @__PURE__ */ React32.createElement(SheetPrimitive.Portal, null, /* @__PURE__ */ React32.createElement(
      SheetPrimitive.Overlay,
      {
        "data-testid": "Overlay",
        ...overrides == null ? void 0 : overrides.Overlay,
        className: classNames(overrides == null ? void 0 : overrides.Overlay.className, overlayStyles)
      }
    ), /* @__PURE__ */ React32.createElement(
      SheetPrimitive.Content,
      {
        className: classNames(
          "fixed z-sheet gap-4 bg-elevation-2 shadow-elevation-2 transition-all ease-in-out",
          "data-[state=closed]:duration-300 data-[state=open]:duration-300",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          sheetVariants({ side }),
          className
        ),
        ref,
        ...props
      },
      content,
      withCloseIcon && /* @__PURE__ */ React32.createElement(
        SheetPrimitive.Close,
        {
          className: classNames(
            "absolute right-5 top-5 flex items-center",
            (side === "top" || side === "bottom") && "right-4 top-6"
          )
        },
        /* @__PURE__ */ React32.createElement(Close, { fill: "interactive-icon-primary-styles" })
      )
    ))
  ));
});
var SheetHeader = ({ className, ...props }) => {
  const { isVerticalSide } = useContext2(SheetContext);
  return /* @__PURE__ */ React32.createElement(
    FlexCenter,
    {
      className: classNames(
        "border-b border-level-2 p-5",
        isVerticalSide && "px-4 py-6",
        className
      ),
      ...props
    }
  );
};
var SheetTitle = forwardRef26(function SheetTitle2({ className, children, ...props }, ref) {
  const { isVerticalSide } = useContext2(SheetContext);
  return /* @__PURE__ */ React32.createElement(SheetPrimitive.Title, { asChild: true, className, ref, ...props }, isVerticalSide ? /* @__PURE__ */ React32.createElement(Text.Heading, { size: "small" }, children) : /* @__PURE__ */ React32.createElement(Text.Body, { weight: "semibold" }, children));
});
var SheetSubtitle = forwardRef26(function SheetSubtitle2({ children, ...props }, ref) {
  return /* @__PURE__ */ React32.createElement(Text.Body, { className: "flex", ref, size: "small", ...props }, children);
});
var SheetBody = forwardRef26(
  function SheetBody2({ children, className, ...props }, ref) {
    const { isVerticalSide } = useContext2(SheetContext);
    return /* @__PURE__ */ React32.createElement(
      Block,
      {
        className: classNames(
          "h-full overflow-y-auto p-5",
          isVerticalSide && "p-4",
          className
        ),
        ref,
        ...props
      },
      children
    );
  }
);
var SheetFooter = forwardRef26(
  function SheetFooter2({ children, className, ...props }, ref) {
    const { isVerticalSide } = useContext2(SheetContext);
    return /* @__PURE__ */ React32.createElement(
      Block,
      {
        asChild: true,
        className: classNames(
          "flex w-full gap-3 p-5",
          isVerticalSide && "flex-col gap-2 p-4",
          className
        ),
        ref,
        ...props
      },
      /* @__PURE__ */ React32.createElement("footer", null, children)
    );
  }
);
var SheetFooterButton = forwardRef26(function SheetFooterButton2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React32.createElement(Button, { className: classNames("w-full", className), ...rest, ref });
});
var Sheet2 = Object.assign(SheetBase, {
  Header: SheetHeader,
  Title: SheetTitle,
  Subtitle: SheetSubtitle,
  Body: SheetBody,
  Footer: Object.assign(SheetFooter, { Button: SheetFooterButton })
});

// src/components/VisuallyHidden/VisuallyHidden.react.tsx
import { Root as VisuallyHiddenRoot } from "@radix-ui/react-visually-hidden";
var VisuallyHidden = VisuallyHiddenRoot;

// src/components/Modal/Modal.react.tsx
var modalVariants = tv14({
  variants: {
    size: {
      small: "w-[360px]",
      medium: "w-[550px]",
      large: "w-[700px]",
      xlarge: "w-[1000px]"
    }
  }
});
var ModalBase = forwardRef27(function Modal({
  open,
  defaultOpen,
  onOpenChange,
  content,
  children,
  className,
  modal,
  withCloseIcon = true,
  overrides,
  size = "medium",
  ...props
}, ref) {
  const [openedState, setOpenedState] = useState6(defaultOpen != null ? defaultOpen : open);
  const resolvedOpenState = open === void 0 ? openedState : open;
  const isControlledWithoutTrigger = open !== void 0 && !children;
  const triggerRef = useRef5(null);
  const didManuallyTriggerRef = useRef5(false);
  const handleOnOpenChange = useCallback4(
    (open2) => {
      if (isControlledWithoutTrigger && !didManuallyTriggerRef.current) {
        return;
      }
      setOpenedState(open2);
      onOpenChange == null ? void 0 : onOpenChange(open2);
      didManuallyTriggerRef.current = false;
    },
    [isControlledWithoutTrigger]
  );
  useEffect5(() => {
    var _a;
    if (!isControlledWithoutTrigger) {
      return;
    }
    if (open) {
      (_a = triggerRef.current) == null ? void 0 : _a.click();
      didManuallyTriggerRef.current = true;
    }
  }, [open, children]);
  return /* @__PURE__ */ React33.createElement(React33.Fragment, null, /* @__PURE__ */ React33.createElement(Media, { lessThan: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
    Sheet2,
    {
      className: classNames(mediaClassName, className),
      content,
      defaultOpen,
      modal,
      open: resolvedOpenState,
      overrides,
      side: "bottom",
      withCloseIcon,
      onOpenChange: handleOnOpenChange
    },
    children
  )), /* @__PURE__ */ React33.createElement(Media, { greaterThanOrEqual: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
    ModalPrimitive.Root,
    {
      defaultOpen,
      modal,
      open: resolvedOpenState,
      onOpenChange: handleOnOpenChange
    },
    /* @__PURE__ */ React33.createElement(ModalPrimitive.Trigger, { asChild: true, ref: triggerRef }, children != null ? children : /* @__PURE__ */ React33.createElement(VisuallyHidden, null, /* @__PURE__ */ React33.createElement(UnstyledButton, null))),
    /* @__PURE__ */ React33.createElement(ModalPrimitive.Portal, null, /* @__PURE__ */ React33.createElement(
      ModalPrimitive.Overlay,
      {
        "data-testid": "Overlay",
        ...overrides == null ? void 0 : overrides.Overlay,
        className: classNames(
          overrides == null ? void 0 : overrides.Overlay.className,
          overlayStyles
        )
      }
    ), /* @__PURE__ */ React33.createElement(
      ModalPrimitive.Content,
      {
        className: classNames(
          "fixed left-[50%] top-[50%] z-modal rounded-3xl border border-level-2 bg-elevation-2 shadow-elevation-2",
          "max-h-[calc(100%-64px)] max-w-[calc(100%-32px)]",
          "translate-x-[-50%] translate-y-[-50%]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:duration-300 data-[state=open]:duration-300",
          "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          modalVariants({ size }),
          mediaClassName,
          className
        ),
        ref,
        ...props
      },
      content,
      withCloseIcon && /* @__PURE__ */ React33.createElement(ModalPrimitive.Close, { className: "absolute right-6 top-8 flex items-center" }, /* @__PURE__ */ React33.createElement(Close, { fill: "interactive-icon-primary-styles" }))
    ))
  )));
});
var ModalHeader = ({ className, ...props }) => {
  return /* @__PURE__ */ React33.createElement(React33.Fragment, null, /* @__PURE__ */ React33.createElement(Media, { lessThan: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
    SheetHeader,
    {
      className: classNames(mediaClassName, className),
      ...props
    }
  )), /* @__PURE__ */ React33.createElement(Media, { greaterThanOrEqual: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
    FlexCenter,
    {
      className: classNames(
        "border-b border-level-2 px-6 pb-4 pt-8",
        mediaClassName,
        className
      ),
      ...props
    }
  )));
};
var ModalHeaderTitle = forwardRef27(function ModalHeaderTitle2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ React33.createElement(React33.Fragment, null, /* @__PURE__ */ React33.createElement(Media, { lessThan: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
    SheetTitle,
    {
      className: classNames(mediaClassName, className),
      ...props
    },
    children
  )), /* @__PURE__ */ React33.createElement(Media, { greaterThanOrEqual: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
    ModalPrimitive.Title,
    {
      asChild: true,
      className: classNames(mediaClassName, className),
      ref,
      ...props
    },
    /* @__PURE__ */ React33.createElement(Text.Heading, { asChild: true, role: "heading", size: "medium" }, /* @__PURE__ */ React33.createElement("h4", null, children))
  )));
});
var ModalBody = forwardRef27(
  function ModalBody2({ className, children, ...props }, ref) {
    return /* @__PURE__ */ React33.createElement(React33.Fragment, null, /* @__PURE__ */ React33.createElement(Media, { lessThan: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
      SheetBody,
      {
        className: classNames(mediaClassName, className),
        ref,
        ...props
      },
      children
    )), /* @__PURE__ */ React33.createElement(Media, { greaterThanOrEqual: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
      Block,
      {
        className: classNames(
          "h-full overflow-y-auto px-6 py-4",
          mediaClassName,
          className
        ),
        ref,
        ...props
      },
      children
    )));
  }
);
var ModalFooter = forwardRef27(
  function ModalFooter2({ children, className, ...props }, ref) {
    return /* @__PURE__ */ React33.createElement(React33.Fragment, null, /* @__PURE__ */ React33.createElement(Media, { lessThan: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
      SheetFooter,
      {
        className: classNames(mediaClassName, className),
        ref,
        ...props
      },
      children
    )), /* @__PURE__ */ React33.createElement(Media, { greaterThanOrEqual: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
      Block,
      {
        asChild: true,
        className: classNames(
          "flex w-full gap-3 p-6",
          mediaClassName,
          className
        ),
        ref,
        ...props
      },
      /* @__PURE__ */ React33.createElement("footer", null, children)
    )));
  }
);
var ModalFooterButton = forwardRef27(function ModalFooterButton2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React33.createElement(React33.Fragment, null, /* @__PURE__ */ React33.createElement(Media, { lessThan: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
    SheetFooterButton,
    {
      className: classNames(mediaClassName, className),
      ref,
      ...rest
    }
  )), /* @__PURE__ */ React33.createElement(Media, { greaterThanOrEqual: "sm" }, (mediaClassName, renderChildren) => renderChildren && /* @__PURE__ */ React33.createElement(
    Button,
    {
      className: classNames("w-full", mediaClassName, className),
      ref,
      ...rest
    }
  )));
});
var Modal2 = Object.assign(ModalBase, {
  Header: Object.assign(ModalHeader, { Title: ModalHeaderTitle }),
  Body: ModalBody,
  Footer: Object.assign(ModalFooter, { Button: ModalFooterButton })
});

// src/components/Checkbox/Checkbox.react.tsx
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import React36, { forwardRef as forwardRef28 } from "react";
import { tv as tv15 } from "tailwind-variants";

// src/icons/material-symbols/outlined/Check.react.tsx
import React34 from "react";
var Check = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React34.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React34.createElement("title", null, "Check"),
    /* @__PURE__ */ React34.createElement("path", { d: "M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" })
  );
};

// src/icons/material-symbols/outlined/HorizontalRule.react.tsx
import React35 from "react";
var HorizontalRule = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React35.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "imt",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React35.createElement("title", null, "Horizontal Rule"),
    /* @__PURE__ */ React35.createElement("path", { d: "M160-440v-80h640v80H160Z" })
  );
};

// src/components/Checkbox/Checkbox.react.tsx
var checkboxVariants = tv15({
  variants: {
    checked: {
      false: "border-level-2",
      true: "border-blue-3 bg-blue-3",
      indeterminate: "border-blue-3 bg-blue-3"
    },
    size: {
      small: "size-5 min-h-[20px] min-w-[20px]",
      medium: "size-6 min-h-[24px] min-w-[24px]"
    }
  }
});
var Checkbox = forwardRef28(function Checkbox2({
  className,
  checked = false,
  disabled,
  size = "medium",
  onCheckedChange,
  ...props
}, ref) {
  const Icon = checked === "indeterminate" ? HorizontalRule : Check;
  return /* @__PURE__ */ React36.createElement(
    CheckboxPrimitive.Root,
    {
      checked,
      className: classNames(
        "rounded-md border outline-none",
        // Only show focus border when keyboard is used, not on mouse click
        "focus:border-level-3 [&:not(:focus-visible)]:border-level-2",
        checkboxVariants({ checked, size }),
        disabledVariants({ disabled }),
        className
      ),
      disabled,
      ref,
      onCheckedChange: () => {
        onCheckedChange == null ? void 0 : onCheckedChange(!checked);
      },
      ...props
    },
    /* @__PURE__ */ React36.createElement(CheckboxPrimitive.Indicator, { className: "flex items-center justify-center" }, /* @__PURE__ */ React36.createElement(Icon, { fill: "white", size: size === "medium" ? 20 : 16 }))
  );
});

// src/components/Dropdown/Dropdown.react.tsx
import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu";
import React39, {
  forwardRef as forwardRef29
} from "react";

// src/components/HydrationGuard/HydrationGuard.react.tsx
import React38 from "react";

// src/hooks/useItemsWithSkeletons.ts
import { useMemo as useMemo6 } from "react";
var useItemsWithSkeletons = ({
  items,
  pageSize,
  isLoading = false,
  isLoadingNext = false
}) => {
  const skeletonCount = useMemo6(() => {
    if (!items || isLoading || isLoadingNext) {
      return pageSize;
    }
    return 0;
  }, [items, isLoading, isLoadingNext, pageSize]);
  const itemsWithSkeletons = useMemo6(() => {
    var _a;
    const length = ((_a = items == null ? void 0 : items.length) != null ? _a : 0) + skeletonCount;
    return Array(length).fill(void 0).map((_, i) => {
      if (isLoading) {
        return void 0;
      }
      return items ? items[i] : void 0;
    });
  }, [items, isLoading, skeletonCount]);
  return itemsWithSkeletons;
};

// src/hooks/useIsHydrated.ts
import { useEffect as useEffect6, useState as useState7 } from "react";
var _isHydrated = false;
var useIsHydrated = () => {
  const [isHydrated, setIsHydrated] = useState7(_isHydrated);
  useEffect6(() => {
    _isHydrated = true;
    if (!isHydrated) {
      setIsHydrated(true);
    }
  }, []);
  return isHydrated;
};

// src/hooks/useDimensions.ts
import { useMemo as useMemo7, useState as useState8 } from "react";

// src/hooks/useIsomorphicLayoutEffect.ts
import { useEffect as useEffect7, useLayoutEffect } from "react";
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect7;

// src/hooks/useDimensions.ts
var DEFAULT_STATE = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0
};
var useDimensions = (ref) => {
  const [dimensions, setDimensions] = useState8(DEFAULT_STATE);
  const node = ref.current;
  const observer = useMemo7(
    () => new ResizeObserver(() => {
      if (node) {
        setDimensions(node.getBoundingClientRect());
      }
    }),
    [node]
  );
  useIsomorphicLayoutEffect(() => {
    if (!node) {
      return;
    }
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [node]);
  return dimensions;
};

// src/hooks/useIsSticky.ts
import { useInView } from "react-intersection-observer";
var useIsSticky = () => {
  const { ref, inView } = useInView({
    rootMargin: "-1px 0px 0px 0px",
    threshold: [1],
    initialInView: true
  });
  return { ref, isSticky: inView === false };
};

// src/hooks/useCallbackRef.ts
import {
  useCallback as useCallback5,
  useRef as useRef6,
  useState as useState9
} from "react";
var useCallbackRef = (customRef, cb) => {
  const ref = useRef6(null);
  const refToUse = customRef != null ? customRef : ref;
  const [_initialize, setInitialize] = useState9(false);
  const setRef = useCallback5(
    (node) => {
      if (refToUse.current) {
        return;
      }
      refToUse.current = node;
      setInitialize(true);
      cb == null ? void 0 : cb(node);
    },
    [refToUse]
  );
  return [refToUse, setRef];
};

// src/components/HydrationGuard/HydrationGuard.react.tsx
var HydrationGuard = ({ fallback, children }) => {
  const isHydrated = useIsHydrated();
  if (!isHydrated) {
    return /* @__PURE__ */ React38.createElement(React38.Fragment, null, fallback || null);
  }
  return /* @__PURE__ */ React38.createElement(React38.Fragment, null, children);
};

// src/components/Dropdown/Dropdown.react.tsx
var DropdownBase = forwardRef29(function Dropdown({
  content,
  open,
  defaultOpen,
  onOpenChange,
  children,
  className,
  overrides,
  forceMount,
  ...props
}, ref) {
  var _a;
  return /* @__PURE__ */ React39.createElement(
    DropdownPrimitive.Root,
    {
      defaultOpen,
      open,
      onOpenChange
    },
    /* @__PURE__ */ React39.createElement(DropdownPrimitive.Trigger, { asChild: true, className, ref }, children),
    /* @__PURE__ */ React39.createElement(HydrationGuard, null, /* @__PURE__ */ React39.createElement(DropdownPrimitive.Portal, { forceMount }, /* @__PURE__ */ React39.createElement(
      DropdownPrimitive.Content,
      {
        className: classNames(
          tooltipVariants({ elevation: "level-3", className: "p-3" }),
          "overflow-auto font-normal",
          "w-[var(--radix-dropdown-menu-trigger-width)]",
          "max-h-[350px] min-w-[220px]",
          (_a = overrides == null ? void 0 : overrides.Content) == null ? void 0 : _a.className
        ),
        sideOffset: 8,
        ...props
      },
      content
    )))
  );
});
var DropdownItemBase = forwardRef29(function DropdownItem({ asChild, children, className, ...rest }, ref) {
  return /* @__PURE__ */ React39.createElement(
    DropdownPrimitive.Item,
    {
      ...rest,
      asChild: true,
      ref,
      onPointerUp: (e) => e.preventDefault()
    },
    /* @__PURE__ */ React39.createElement(
      Item2,
      {
        asChild,
        className: classNames(
          "rounded-xl border-0 px-3 focus:bg-component-gray-1 focus:outline-none",
          className
        ),
        interactive: true,
        ref
      },
      children
    )
  );
});
var DropdownItemAvatar = Item2.Avatar;
var DropdownItemTitle = Item2.Title;
var DropdownItemDescription = Item2.Description;
var DropdownItemContent = Item2.Content;
var DropdownItem2 = Object.assign(DropdownItemBase, {
  Avatar: DropdownItemAvatar,
  Title: DropdownItemTitle,
  Description: DropdownItemDescription,
  Content: DropdownItemContent
});
var DropdownSeparator = forwardRef29(function DropdownSeparator2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React39.createElement(Separator, { className: classNames("my-1", className), ...rest, ref });
});
var Dropdown2 = Object.assign(DropdownBase, {
  Item: DropdownItem2,
  Separator: DropdownSeparator
});

// src/components/FloatingCombobox/FloatingCombobox.react.tsx
import React50, { forwardRef as forwardRef31 } from "react";

// src/components/Select/Select.react.tsx
import React49, {
  Children,
  forwardRef as forwardRef30,
  useLayoutEffect as useLayoutEffect2,
  useMemo as useMemo8,
  useState as useState10
} from "react";
import { createContext as createContext6, useContextSelector as useContextSelector6 } from "use-context-selector";

// src/icons/material-symbols/outlined/CheckCircleFilled.react.tsx
import React40 from "react";
var CheckCircleFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React40.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React40.createElement("title", null, "Check Circle"),
    /* @__PURE__ */ React40.createElement("path", { d: "m424-296 282-282-56-56-226 226-114-114-56 56 170 170Zm56 216q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/CircleFilled.react.tsx
import React41 from "react";
var CircleFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React41.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React41.createElement("title", null, "Circle Filled"),
    /* @__PURE__ */ React41.createElement("path", { d: "M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-156t86-127Q252-817 325-848.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 82-31.5 155T763-197.5q-54 54.5-127 86T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/Error.react.tsx
import React42 from "react";
var Error2 = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Error",
  ...rest
}) => {
  return /* @__PURE__ */ React42.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React42.createElement("title", null, title),
    /* @__PURE__ */ React42.createElement("path", { d: "M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" })
  );
};

// src/icons/material-symbols/outlined/ErrorFilled.react.tsx
import React43 from "react";
var ErrorFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Error",
  ...rest
}) => {
  return /* @__PURE__ */ React43.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React43.createElement("title", null, title),
    /* @__PURE__ */ React43.createElement("path", { d: "M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/ExpandAll.react.tsx
import React44 from "react";
var ExpandAll = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React44.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React44.createElement("title", null, "Expand all"),
    /* @__PURE__ */ React44.createElement("path", { d: "M480-80 240-320l57-57 183 183 183-183 57 57L480-80ZM298-584l-58-56 240-240 240 240-58 56-182-182-182 182Z" })
  );
};

// src/icons/material-symbols/outlined/ExpandLess.react.tsx
import React45 from "react";
var ExpandLess = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React45.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React45.createElement("title", null, "Expand less"),
    /* @__PURE__ */ React45.createElement("path", { d: "m296-345-56-56 240-240 240 240-56 56-184-184-184 184Z" })
  );
};

// src/icons/material-symbols/outlined/ExpandMore.react.tsx
import React46 from "react";
var ExpandMore = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React46.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React46.createElement("title", null, "Expand more"),
    /* @__PURE__ */ React46.createElement("path", { d: "M480-345 240-585l56-56 184 184 184-184 56 56-240 240Z" })
  );
};

// src/icons/material-symbols/outlined/InfoFilled.react.tsx
import React47 from "react";
var InfoFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React47.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React47.createElement("title", null, "Info"),
    /* @__PURE__ */ React47.createElement("path", { d: "M440-280h80v-240h-80v240Zm40-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" })
  );
};

// src/icons/material-symbols/outlined/WarningFilled.react.tsx
import React48 from "react";
var WarningFilled = ({
  size = 24,
  fill: fill2 = "current",
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React48.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React48.createElement("title", null, "Warning"),
    /* @__PURE__ */ React48.createElement("path", { d: "m40-120 440-760 440 760H40Zm440-120q17 0 28.5-11.5T520-280q0-17-11.5-28.5T480-320q-17 0-28.5 11.5T440-280q0 17 11.5 28.5T480-240Zm-40-120h80v-200h-80v200Z" })
  );
};

// src/components/Select/Select.react.tsx
var SelectContext = createContext6({
  selection: "single",
  itemRefs: [],
  setItemRefs: () => {
  },
  onValueChange: () => {
  },
  forceMount: void 0,
  setForceMount: () => {
  }
});
var SelectBase = forwardRef30(function Select({
  value,
  children,
  className,
  placeholder,
  disabled,
  error,
  id,
  name,
  renderValue,
  ...props
}, ref) {
  const [itemRefs, setItemRefs, forceMount, setForceMount] = useContextSelector6(
    SelectContext,
    (v) => [v.itemRefs, v.setItemRefs, v.forceMount, v.setForceMount]
  );
  useLayoutEffect2(() => {
    setForceMount(void 0);
  }, [forceMount]);
  useLayoutEffect2(() => {
    setItemRefs([]);
    if (forceMount) {
      setForceMount(void 0);
    } else {
      setForceMount(true);
    }
  }, [Children.toArray(children).length]);
  const displayText = useMemo8(() => {
    var _a, _b;
    return (_b = (_a = itemRefs.find((el) => el.value === value)) == null ? void 0 : _a.querySelector("[data-item-text]")) == null ? void 0 : _b.textContent;
  }, [itemRefs, value]);
  return /* @__PURE__ */ React49.createElement(Dropdown2, { content: children, forceMount, ref, ...props }, /* @__PURE__ */ React49.createElement(
    SelectButton,
    {
      className,
      disabled,
      error,
      id,
      name,
      placeholder
    },
    renderValue && value ? renderValue(value) : displayText || value || placeholder
  ));
});
var SelectBaseWithContext = forwardRef30(function SelectWithContext({ selection = "single", onValueChange, ...rest }, ref) {
  const [itemRefs, setItemRefs] = useState10([]);
  const [forceMount, setForceMount] = useState10(true);
  return /* @__PURE__ */ React49.createElement(
    SelectContext.Provider,
    {
      value: {
        selection,
        itemRefs,
        setItemRefs,
        onValueChange,
        forceMount,
        setForceMount
      }
    },
    /* @__PURE__ */ React49.createElement(SelectBase, { ...rest, ref })
  );
});
var SelectButton = forwardRef30(function SelectButton2({ children, className, disabled, error, ...rest }, ref) {
  return /* @__PURE__ */ React49.createElement(
    UnstyledButton,
    {
      className: classNames(
        "justify-between focus:outline-none",
        "[&[data-state=closed]>svg]:-rotate-90 [&[data-state=open]>svg]:rotate-90",
        inputVariants({ disabled, error }),
        className
      ),
      disabled,
      ref,
      ...rest
    },
    /* @__PURE__ */ React49.createElement(Text, { weight: "semibold" }, children),
    /* @__PURE__ */ React49.createElement(ChevronLeft, { className: "duration-[time:250ms] transition" })
  );
});
var SelectItemBase = forwardRef30(function SelectItem({ value, onSelect, ...rest }, ref) {
  const [itemRefs, setItemRefs, onValueChange, selection] = useContextSelector6(
    SelectContext,
    (v) => [v.itemRefs, v.setItemRefs, v.onValueChange, v.selection]
  );
  return /* @__PURE__ */ React49.createElement(
    DropdownItem2,
    {
      ...rest,
      ref: (el) => {
        if (ref) {
          if (typeof ref === "function") {
            ref(el);
          } else {
            ref.current = el;
          }
        }
        if (el instanceof HTMLButtonElement && !itemRefs.map((item) => item.value).includes(el.value)) {
          el.value = value;
          setItemRefs((itemRefs2) => [...itemRefs2, el]);
        }
      },
      onSelect: (e) => {
        onSelect == null ? void 0 : onSelect(e);
        onValueChange == null ? void 0 : onValueChange(value);
        if (selection === "multiple") {
          e.preventDefault();
        }
      }
    }
  );
});
var SelectItemAvatar = DropdownItemAvatar;
var SelectItemTitle = (props) => {
  return /* @__PURE__ */ React49.createElement(ItemTitle, { "data-item-text": true, ...props });
};
var SelectItemDescription = DropdownItemDescription;
var SelectItemContent = DropdownItemContent;
var SelectItem2 = Object.assign(SelectItemBase, {
  Avatar: SelectItemAvatar,
  Title: SelectItemTitle,
  Description: SelectItemDescription,
  Content: SelectItemContent
});
var Select2 = Object.assign(SelectBaseWithContext, {
  Item: SelectItem2
});

// src/components/FloatingCombobox/FloatingCombobox.react.tsx
var FloatingComboboxBase = forwardRef31(function FloatingCombobox({
  children,
  open,
  onOpenChange,
  defaultOpen,
  content,
  shouldFilter,
  filter,
  label,
  ...rest
}, ref) {
  return /* @__PURE__ */ React50.createElement(
    Popover,
    {
      content: /* @__PURE__ */ React50.createElement(Command2, { filter, label, shouldFilter }, content),
      defaultOpen,
      open,
      onOpenChange
    },
    /* @__PURE__ */ React50.createElement(SelectButton, { ...rest, ref }, children)
  );
});
var FloatingComboboxInput = CommandInput;
var FloatingComboboxList = CommandList;
var FloatingComboboxEmpty = CommandEmpty;
var FloatingComboboxItemBase = CommandItem2;
var FloatingComboboxItemAvatar = CommandItemAvatar;
var FloatingComboboxItemTitle = CommandItemTitle;
var FloatingComboboxItemDescription = CommandItemDescription;
var FloatingComboboxItemContent = CommandItemDescription;
var FloatingComboboxItem = Object.assign(
  {},
  FloatingComboboxItemBase,
  {
    Avatar: FloatingComboboxItemAvatar,
    Title: FloatingComboboxItemTitle,
    Description: FloatingComboboxItemDescription,
    Content: FloatingComboboxItemContent
  }
);
var FloatingCombobox2 = Object.assign(FloatingComboboxBase, {
  List: FloatingComboboxList,
  Empty: FloatingComboboxEmpty,
  Item: FloatingComboboxItem,
  Input: FloatingComboboxInput
});

// src/components/FormControl/FormControl.react.tsx
import React53, { forwardRef as forwardRef34, cloneElement } from "react";

// src/components/Label/Label.react.tsx
import * as LabelPrimitive from "@radix-ui/react-label";
import React51, { forwardRef as forwardRef32 } from "react";
var Label = forwardRef32(function Label2({
  className,
  size = "medium",
  weight = "semibold",
  required,
  visuallyHidden,
  ...props
}, ref) {
  const content = /* @__PURE__ */ React51.createElement(Text.Body, { asChild: true, size, weight }, /* @__PURE__ */ React51.createElement(
    LabelPrimitive.Root,
    {
      className: classNames(required && "after:content-['_*']", className),
      ref,
      ...props
    }
  ));
  if (visuallyHidden) {
    return /* @__PURE__ */ React51.createElement(VisuallyHidden, null, content);
  }
  return content;
});

// src/components/SpaceBetween/SpaceBetween.react.tsx
import React52, { forwardRef as forwardRef33 } from "react";
var SpaceBetween = forwardRef33(
  function SpaceBetween2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React52.createElement(
      Flex,
      {
        className: classNames("justify-between", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/FormControl/FormControl.react.tsx
var FormControl = forwardRef34(function FormControl2({
  children,
  label,
  htmlFor,
  caption,
  error,
  warning,
  disabled = false,
  required,
  hideLabel = false,
  tip,
  inline = false,
  ...rest
}, ref) {
  const onlyChildrenProps = children.props;
  const isCaptionString = typeof caption === "string";
  const isLabelFunction = typeof label === "function";
  const errorElement = error || warning;
  const htmlForWithFallback = htmlFor || onlyChildrenProps.id;
  const childrenProps = {};
  if (onlyChildrenProps.disabled || !!disabled) {
    childrenProps["disabled"] = true;
  }
  if (onlyChildrenProps.error || !!error) {
    childrenProps["error"] = true;
  }
  const content = cloneElement(children, childrenProps);
  return /* @__PURE__ */ React53.createElement(FlexColumn, { ...rest, ref }, /* @__PURE__ */ React53.createElement(
    Flex,
    {
      className: inline ? "flex-row items-center justify-between" : "flex-col"
    },
    /* @__PURE__ */ React53.createElement(FlexColumn, { className: hideLabel || inline ? void 0 : "mb-3" }, isLabelFunction ? label({
      htmlFor: htmlForWithFallback,
      required,
      visuallyHidden: hideLabel
    }) : /* @__PURE__ */ React53.createElement(
      Label,
      {
        className: "w-full",
        htmlFor: htmlForWithFallback,
        required,
        visuallyHidden: hideLabel
      },
      label
    ), tip && /* @__PURE__ */ React53.createElement(Text.Body, { className: "mt-1 text-secondary", size: "tiny" }, tip)),
    inline ? /* @__PURE__ */ React53.createElement(CenterAligned, null, content) : content
  ), /* @__PURE__ */ React53.createElement(SpaceBetween, { className: "mt-1" }, errorElement && /* @__PURE__ */ React53.createElement(
    Caption,
    {
      "aria-describedby": htmlForWithFallback,
      error: !!error,
      role: "status",
      warning: !!warning
    },
    /* @__PURE__ */ React53.createElement(
      Error2,
      {
        className: classNames(
          "mr-1 !leading-[inherit]",
          warning && "text-warning"
        ),
        size: 14,
        title: error ? "Error" : "Warning"
      }
    ),
    /* @__PURE__ */ React53.createElement("span", null, errorElement)
  ), isCaptionString ? !errorElement && /* @__PURE__ */ React53.createElement(Caption, { "aria-describedby": htmlForWithFallback }, caption) : caption));
});
var Caption = ({ className, error, warning, ...rest }) => /* @__PURE__ */ React53.createElement(
  Text.Body,
  {
    className: classNames(
      "flex w-full",
      error && "text-error",
      warning && !error && "text-primary",
      className
    ),
    color: "secondary",
    size: "small",
    ...rest
  }
);

// src/components/InputGroup/InputGroup.react.tsx
import React54 from "react";
var InputGroupInput = Input;
var InputGroupBase = ({ children, className, ...rest }) => {
  return /* @__PURE__ */ React54.createElement(
    "div",
    {
      className: classNames(
        "[&>*]:mt-[-1px] [&>*]:rounded-none",
        "first:[&>*]:rounded-t-xl last:[&>*]:rounded-b-xl",
        "hover:[&>*]:relative hover:[&>*]:z-[1]",
        className
      ),
      ...rest
    },
    children
  );
};
var InputGroup = Object.assign(InputGroupBase, {
  Input: InputGroupInput
});

// src/components/PhoneInput/PhoneInput.react.tsx
import useMergedRef3 from "@react-hook/merged-ref";
import React55, { forwardRef as forwardRef35, useCallback as useCallback6, useEffect as useEffect8, useMemo as useMemo9 } from "react";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput
} from "react-international-phone";
var PhoneInput = forwardRef35(
  function PhoneInput2({
    value,
    onChange,
    onValueChange,
    onCountryChange,
    defaultCountry = "us",
    supportedCountries,
    className,
    disabled,
    error,
    overrides,
    ...inputProps
  }, ref) {
    var _a;
    const countries = useMemo9(() => {
      if (!supportedCountries) {
        return defaultCountries;
      }
      return defaultCountries.filter((country2) => {
        const { iso2 } = parseCountry(country2);
        return supportedCountries.includes(iso2);
      });
    }, [supportedCountries]);
    const {
      inputValue,
      handlePhoneValueChange: localOnChange,
      inputRef: localInputRef,
      country,
      setCountry
    } = usePhoneInput({
      countries,
      defaultCountry,
      value: value == null ? void 0 : value.toString(),
      onChange: ({ phone }) => {
        onValueChange == null ? void 0 : onValueChange(phone);
      }
    });
    const handleChange = useCallback6(
      (e) => {
        localOnChange(e);
        onChange == null ? void 0 : onChange(e);
      },
      [localOnChange, onChange]
    );
    const handleCountryChange = useCallback6(
      (iso2) => {
        setCountry(iso2);
      },
      [setCountry]
    );
    useEffect8(() => {
      onCountryChange == null ? void 0 : onCountryChange(country.iso2);
    }, [country]);
    const countryOptions = useMemo9(
      () => countries.map((supportedCountry) => {
        const { dialCode, iso2, name } = parseCountry(supportedCountry);
        return /* @__PURE__ */ React55.createElement(Select2.Item, { className: "w-full", key: iso2, value: iso2 }, /* @__PURE__ */ React55.createElement(Select2.Item.Avatar, { className: "mr-2" }, /* @__PURE__ */ React55.createElement(FlagImage, { iso2 })), /* @__PURE__ */ React55.createElement(Select2.Item.Content, { className: "flex-row items-center justify-start gap-1" }, /* @__PURE__ */ React55.createElement(Select2.Item.Title, { size: "small" }, name), /* @__PURE__ */ React55.createElement(Select2.Item.Description, { color: "secondary", size: "small" }, "+", dialCode)));
      }),
      [countries]
    );
    const {
      className: selectClassName,
      overrides: selectOverrides,
      ...selectOverrideProps
    } = (overrides == null ? void 0 : overrides.Select) || {};
    return /* @__PURE__ */ React55.createElement(
      Input,
      {
        ...inputProps,
        autoComplete: "tel-national",
        className: classNames("pl-0", className),
        disabled,
        error,
        inputRef: useMergedRef3(ref, localInputRef),
        startEnhancer: /* @__PURE__ */ React55.createElement(
          Select2,
          {
            align: "start",
            className: classNames(
              "ml-[-1px] mr-4 w-fit min-w-fit rounded-r-none border-y-0 border-l-0 border-r pr-1",
              selectClassName
            ),
            disabled,
            overrides: {
              ...selectOverrides,
              Content: {
                ...selectOverrides == null ? void 0 : selectOverrides.Content,
                className: classNames(
                  "w-full",
                  (_a = selectOverrides == null ? void 0 : selectOverrides.Content) == null ? void 0 : _a.className
                )
              }
            },
            renderValue: (value2) => /* @__PURE__ */ React55.createElement(FlagImage, { className: "mr-1", iso2: value2, width: "24px" }),
            value: country.iso2,
            onValueChange: handleCountryChange,
            ...selectOverrideProps
          },
          countryOptions
        ),
        type: "tel",
        value: inputValue,
        onChange: handleChange
      }
    );
  }
);

// src/components/Progress/Progress.react.tsx
import * as ProgressPrimitive from "@radix-ui/react-progress";
import React56, { forwardRef as forwardRef36 } from "react";
import { tv as tv16 } from "tailwind-variants";
var progressVariants = tv16({
  variants: {
    interactive: {
      true: "hover:bg-component-gray-2"
    },
    variant: {
      default: "bg-component-gray-1",
      translucent: "bg-white/30"
    }
  }
});
var indicatorVariants = tv16({
  variants: {
    variant: {
      default: "bg-gradient-to-r from-blue-1 to-blue-3",
      translucent: "bg-white"
    }
  }
});
var Progress = forwardRef36(function Progress2({ className, interactive, variant = "default", value, ...props }, ref) {
  return /* @__PURE__ */ React56.createElement(
    ProgressPrimitive.Root,
    {
      className: classNames(
        "relative h-4 w-full overflow-hidden rounded-full",
        progressVariants({ interactive, variant }),
        className
      ),
      ref,
      ...props
    },
    /* @__PURE__ */ React56.createElement(
      ProgressPrimitive.Indicator,
      {
        className: classNames(
          "size-full flex-1 rounded-full transition-all",
          indicatorVariants({ variant })
        ),
        style: { transform: `translateX(-${100 - (value != null ? value : 0)}%)` }
      }
    )
  );
});

// src/components/RadioButtonGroup/RadioButtonGroup.react.tsx
import React58 from "react";

// src/components/RadioGroup/RadioGroup.react.tsx
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import React57, { forwardRef as forwardRef37 } from "react";
var RadioGroupBase = forwardRef37(function RadioGroupBase2({ className, ...props }, ref) {
  return /* @__PURE__ */ React57.createElement(
    RadioGroupPrimitive.Root,
    {
      className: classNames("grid gap-4", className),
      ...props,
      ref
    }
  );
});
var RadioGroupItem = forwardRef37(function RadioGroupItem2({ className, disabled, ...props }, ref) {
  return /* @__PURE__ */ React57.createElement(
    RadioGroupPrimitive.Item,
    {
      className: classNames(
        "mr-4 size-6 min-h-[24px] min-w-[24px] rounded-full border outline-none",
        // Relying directly on data attributes because checked state is derived from the RadioGroup root
        "data-[state=unchecked]:border-level-2 data-[state=unchecked]:focus:border-level-3",
        "data-[state=checked]:border-blue-3",
        disabledVariants({ disabled }),
        className
      ),
      disabled,
      ref,
      value: "value" in props ? props.value : "",
      ...props
    },
    /* @__PURE__ */ React57.createElement(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center" }, /* @__PURE__ */ React57.createElement(CircleFilled, { fill: "blue-3", size: 14 }))
  );
});
var RadioGroup = Object.assign(RadioGroupBase, {
  Item: RadioGroupItem
});

// src/components/RadioButtonGroup/RadioButtonGroup.react.tsx
var RadioButtonGroupBase = ({
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React58.createElement(RadioGroup, { className: classNames("gap-0", className), ...rest });
};
var RadioButtonGroupItemBase = ({
  children,
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React58.createElement(
    Item2,
    {
      asChild: true,
      className: classNames(
        "cursor-pointer transition-all duration-200 hover:shadow-elevation-1",
        "[&:has(button[data-disabled])]:pointer-events-none",
        "first:rounded-t-2xl last:rounded-b-2xl [&:not(:last-child)]:border-b-0",
        className
      )
    },
    /* @__PURE__ */ React58.createElement(Label, { htmlFor: rest.id }, children, /* @__PURE__ */ React58.createElement(Item2.Side, null, /* @__PURE__ */ React58.createElement(RadioGroup.Item, { className: "m-0", ...rest })))
  );
};
var RadioButtonGroupItemIcon = ({
  className,
  value,
  ...rest
}) => {
  return /* @__PURE__ */ React58.createElement(
    Item2.Avatar,
    {
      className: classNames("self-start", className),
      icon: value,
      ...rest
    }
  );
};
var RadioButtonGroupItemContent = ({
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React58.createElement(Item2.Content, { className: classNames("gap-1", className), ...rest });
};
var RadioButtonGroupItemTitle = Item2.Title;
var RadioButtonGroupItemDescription = ({
  size = "medium",
  weight = "semibold",
  ...rest
}) => {
  return /* @__PURE__ */ React58.createElement(Item2.Description, { size, weight, ...rest });
};
var RadioButtonGroup = Object.assign(RadioButtonGroupBase, {
  Item: Object.assign(RadioButtonGroupItemBase, {
    Icon: RadioButtonGroupItemIcon,
    Content: RadioButtonGroupItemContent,
    Title: RadioButtonGroupItemTitle,
    Description: RadioButtonGroupItemDescription
  })
});

// src/components/ScrollingPaginator/ScrollingPaginator.react.tsx
import React59, { memo } from "react";

// src/components/ScrollingPaginator/useScrollingPagination.ts
import { useEffect as useEffect9, useRef as useRef7, useState as useState11 } from "react";
import { useInView as useInView2 } from "react-intersection-observer";
var useScrollingPagination = ({
  hasNext,
  loadNext,
  isLoadingNext,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState11(isLoadingNext);
  const idleCallbackRef = useRef7();
  const { ref, inView } = useInView2({
    skip: !hasNext,
    ...rest
  });
  useEffect9(() => {
    if (inView && !isLoading && hasNext) {
      setIsLoading(true);
      Promise.resolve(loadNext()).then(() => {
        idleCallbackRef.current = requestIdleCallback(() => {
          idleCallbackRef.current = void 0;
          setIsLoading(false);
        });
      }).catch(() => {
        setIsLoading(false);
      });
    }
  }, [inView, loadNext, isLoading, hasNext]);
  useEffect9(() => {
    return () => {
      if (idleCallbackRef.current) {
        cancelIdleCallback(idleCallbackRef.current);
      }
    };
  }, []);
  return { ref, isLoading };
};

// src/components/ScrollingPaginator/ScrollingPaginator.react.tsx
var ScrollingPaginator = memo(function ScrollingPaginator2({
  renderFallback: Fallback,
  ...pagination
}) {
  const { ref, isLoading } = useScrollingPagination(pagination);
  const renderContent = () => {
    if (!isLoading) {
      return /* @__PURE__ */ React59.createElement("div", { style: { height: "1px" } });
    }
    if (Fallback) {
      return /* @__PURE__ */ React59.createElement(Fallback, null);
    }
    return null;
  };
  return /* @__PURE__ */ React59.createElement("div", { ref }, renderContent());
});

// src/components/Sidebar/Sidebar.react.tsx
import React60, {
  createContext as createContext7,
  forwardRef as forwardRef38,
  useContext as useContext3,
  useState as useState12
} from "react";
import { tv as tv17 } from "tailwind-variants";
var sidebarVariants = tv17({
  base: "border-0 border-level-1",
  variants: {
    side: {
      left: "border-r",
      right: "border-l"
    }
  }
});
var hoverWidthClassName = classNames("hover:w-[222px]");
var SidebarContext = createContext7({
  // This is used to prevent the sidebar from expanding after user clicks on an item.
  // After moving their mouse out of the sidebar, it can expand again
  disableExpand: false,
  setDisableExpand: (_disableHover) => {
  }
});
var SidebarBase = forwardRef38(function Sidebar({ className, children, side = "left", fillExpandedSpace = false, ...rest }, ref) {
  const [disableExpand, setDisableExpand] = useState12(false);
  return /* @__PURE__ */ React60.createElement(SidebarContext.Provider, { value: { disableExpand, setDisableExpand } }, /* @__PURE__ */ React60.createElement(
    "div",
    {
      className: classNames(
        "min-w-[72px] transition-width",
        fillExpandedSpace && !disableExpand && hoverWidthClassName
      )
    },
    /* @__PURE__ */ React60.createElement(
      "aside",
      {
        className: classNames(
          "group fixed inset-y-0 z-sidebar h-full",
          side === "right" && "right-0"
        ),
        ref,
        onMouseLeave: () => {
          setDisableExpand(false);
        }
      },
      /* @__PURE__ */ React60.createElement(
        "nav",
        {
          className: classNames(
            "flex h-full w-[73px] flex-col gap-4 px-4 py-6 transition-width",
            "bg-component-gray-2-solid backdrop-blur-[60px]",
            !disableExpand && hoverWidthClassName,
            sidebarVariants({ side }),
            className
          ),
          ...rest
        },
        children
      ),
      /* @__PURE__ */ React60.createElement(
        "div",
        {
          className: classNames(
            overlayStyles,
            "pointer-events-none z-[-1] bg-transparent transition-all",
            !disableExpand && "group-hover:bg-gray-3/80"
          )
        }
      )
    )
  ));
});
var sidebarItemVariants = tv17({
  variants: {
    interactive: {
      true: "text-secondary hover:text-primary"
    },
    active: {
      true: "bg-component-gray-2 text-primary"
    }
  }
});
var SidebarItemBase = forwardRef38(
  function SidebarItem({ active, children, renderItem, className, interactive, onClick, ...rest }, ref) {
    const { setDisableExpand } = useContext3(SidebarContext);
    const Comp = renderItem != null ? renderItem : Item2;
    const isInteractive = interactive != null ? interactive : isInteractiveElement({ ...rest, onClick });
    return /* @__PURE__ */ React60.createElement(
      Comp,
      {
        className: classNames(
          "h-[40px] w-full rounded bg-transparent p-2",
          "flex flex-nowrap items-center",
          sidebarItemVariants({ active, interactive: isInteractive }),
          className
        ),
        interactive: isInteractive,
        ref,
        variant: "unstyled",
        onClick: (e) => {
          onClick == null ? void 0 : onClick(e);
          setDisableExpand(true);
        },
        ...rest
      },
      children
    );
  }
);
var SidebarAvatar = ({
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React60.createElement(Item2.Avatar, { className: classNames("text-current", className), ...rest });
};
var SidebarItemTitle = ({
  children,
  className
}) => {
  return /* @__PURE__ */ React60.createElement(Item2.Content, null, /* @__PURE__ */ React60.createElement(
    Item2.Title,
    {
      className: classNames(
        "whitespace-nowrap font-inherit opacity-0 transition-opacity group-hover:opacity-100",
        className
      ),
      color: "current"
    },
    children
  ));
};
var SidebarItem2 = Object.assign(SidebarItemBase, {
  Avatar: SidebarAvatar,
  Title: SidebarItemTitle
});
var Sidebar2 = Object.assign(SidebarBase, {
  Item: SidebarItem2
});

// src/components/Slider/Slider.react.tsx
import * as SliderPrimitive from "@radix-ui/react-slider";
import React61, { forwardRef as forwardRef39 } from "react";
var Slider = forwardRef39(function Slider2({
  className,
  defaultValue,
  value,
  disabled,
  onValueChange,
  error,
  minStepsBetweenThumbs = 1,
  ...props
}, ref) {
  return /* @__PURE__ */ React61.createElement(
    FlexCenter,
    {
      className: classNames(
        inputVariants({ error, disabled }),
        "p-3",
        className
      )
    },
    /* @__PURE__ */ React61.createElement(
      SliderPrimitive.Root,
      {
        className: "relative flex w-full cursor-pointer touch-none select-none items-center",
        defaultValue: defaultValue ? [defaultValue] : void 0,
        disabled,
        minStepsBetweenThumbs,
        ref,
        value: value ? [value] : void 0,
        onValueChange: ([value2]) => {
          onValueChange == null ? void 0 : onValueChange(value2);
        },
        ...props
      },
      /* @__PURE__ */ React61.createElement(
        SliderPrimitive.Track,
        {
          className: classNames(
            "relative h-2 w-full grow overflow-hidden",
            "rounded-full bg-component-gray-2"
          )
        },
        /* @__PURE__ */ React61.createElement(SliderPrimitive.Range, { className: "absolute h-full bg-blue-3" })
      ),
      /* @__PURE__ */ React61.createElement(
        SliderPrimitive.Thumb,
        {
          className: classNames(
            "block size-4 rounded-full bg-blue-3 shadow-elevation-1",
            "transition-colors focus-visible:outline-none"
          )
        }
      )
    )
  );
});

// src/components/Switch/Switch.react.tsx
import * as SwitchPrimitives from "@radix-ui/react-switch";
import React62, { forwardRef as forwardRef40 } from "react";
import { tv as tv18 } from "tailwind-variants";
var switchVariants = tv18({
  variants: {
    checked: {
      false: "bg-component-gray-3 hover:bg-component-gray-4 active:bg-component-gray-4",
      true: "bg-blue-3 hover:bg-blue-4 active:bg-blue-4"
    }
  }
});
var thumbVariants = tv18({
  variants: {
    checked: {
      false: "translate-x-0",
      true: "translate-x-6"
    }
  }
});
var Switch = forwardRef40(function Switch2({ checked = false, disabled, ...rest }, ref) {
  return /* @__PURE__ */ React62.createElement(
    SwitchPrimitives.Root,
    {
      checked,
      className: classNames(
        "inline-flex h-6 min-h-[24px] w-12 min-w-[48px] shrink-0 cursor-pointer items-center rounded-full border-[2px] border-transparent transition-colors",
        switchVariants({ checked }),
        disabledVariants({ disabled })
      ),
      disabled,
      ref,
      ...rest
    },
    /* @__PURE__ */ React62.createElement(
      SwitchPrimitives.Thumb,
      {
        className: classNames(
          "pointer-events-none block size-5 rounded-full bg-white transition-transform",
          thumbVariants({ checked })
        )
      }
    )
  );
});

// src/components/TextOverflow/TextOverflow.react.tsx
import React64, { useRef as useRef8, useState as useState13 } from "react";
import { tv as tv19 } from "tailwind-variants";

// src/components/Tooltip/Tooltip.react.tsx
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import React63, { forwardRef as forwardRef41 } from "react";
var Tooltip = forwardRef41(function Tooltip2({
  open,
  defaultOpen,
  onOpenChange,
  content,
  children,
  className,
  disabled,
  delayDuration = 0,
  disableHoverableContent,
  elevation = "level-3",
  align = "center",
  variant = "default",
  arrow = variant === "default",
  sideOffset = variant === "translucent" ? 8 : 4,
  animation = "fade-in",
  ...props
}, ref) {
  return /* @__PURE__ */ React63.createElement(TooltipPrimitive.Provider, null, /* @__PURE__ */ React63.createElement(
    TooltipPrimitive.Root,
    {
      defaultOpen,
      delayDuration,
      disableHoverableContent,
      open,
      onOpenChange
    },
    /* @__PURE__ */ React63.createElement(TooltipPrimitive.Trigger, { asChild: true }, children),
    /* @__PURE__ */ React63.createElement(TooltipPrimitive.Portal, null, !disabled && /* @__PURE__ */ React63.createElement(
      TooltipPrimitive.Content,
      {
        align,
        className: classNames(
          tooltipVariants({ elevation, variant, animation }),
          className
        ),
        ref,
        sideOffset,
        ...props
      },
      arrow && /* @__PURE__ */ React63.createElement(
        TooltipPrimitive.Arrow,
        {
          className: tooltipArrowVariants({ elevation })
        }
      ),
      content
    ))
  ));
});

// src/components/TextOverflow/TextOverflow.react.tsx
var getIsOverflowed = (element) => {
  if (!element) {
    return false;
  }
  const { clientHeight, clientWidth, scrollHeight, scrollWidth } = element;
  return scrollHeight > clientHeight || scrollWidth > clientWidth;
};
var textOverflowVariants = tv19({
  variants: {
    lines: {
      1: "line-clamp-1",
      2: "line-clamp-2",
      3: "line-clamp-3",
      4: "line-clamp-4",
      5: "line-clamp-5",
      6: "line-clamp-6"
    }
  }
});
var TextOverflow = ({
  className,
  children,
  overrides,
  lines,
  ...rest
}) => {
  const ref = useRef8(null);
  const [overflowed, setOverflowed] = useState13(false);
  return /* @__PURE__ */ React64.createElement(Tooltip, { content: children, disabled: !overflowed, ...overrides == null ? void 0 : overrides.Tooltip }, /* @__PURE__ */ React64.createElement(
    "div",
    {
      className: classNames(
        "w-full",
        !lines && "truncate",
        textOverflowVariants({ lines }),
        className
      ),
      ref,
      tabIndex: !overflowed ? -1 : 0,
      onMouseOver: () => {
        const isOverflowed = getIsOverflowed(ref == null ? void 0 : ref.current);
        setOverflowed(isOverflowed);
      },
      ...rest
    },
    children
  ));
};

// src/components/Toast/Toast.react.tsx
import * as ToastPrimitives from "@radix-ui/react-toast";
import React65, { forwardRef as forwardRef42 } from "react";

// src/components/Toast/useToasts.ts
import { createContext as createContext8, useContext as useContext4 } from "react";
var ToastContext = createContext8({
  addToasts: () => null,
  removeToast: () => null,
  showErrorMessage: () => null,
  showInfoMessage: () => null,
  showSuccessMessage: () => null,
  showWarningMessage: () => null,
  toastYOffset: void 0,
  setToastYOffset: () => null,
  resetToastYOffset: () => null,
  toastXOffset: void 0,
  setToastXOffset: () => null,
  resetToastXOffset: () => null
});
var useToasts = () => useContext4(ToastContext);

// src/components/Toast/Toast.react.tsx
var ANIMATION_DURATION_MS = 300;
var Toast = forwardRef42(function Toast2({ timeout, id, content, icon, action, onClick }, ref) {
  const { removeToast } = useToasts();
  const showClose = !timeout;
  return /* @__PURE__ */ React65.createElement(
    ToastPrimitives.Root,
    {
      className: classNames(
        "w-fit max-w-full rounded-xl bg-elevation-3 p-4 shadow-elevation-3 focus:outline-none",
        "text-sm font-semibold sm:text-md",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out data-[state=open]:fade-in",
        "data-[swipe=cancel]:translate-x-0",
        "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
        "data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:animate-out",
        "data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full",
        onClick && "cursor-pointer"
      ),
      duration: timeout || Number.POSITIVE_INFINITY,
      id,
      ref,
      style: { animationDuration: `${ANIMATION_DURATION_MS}ms` },
      onClick,
      onOpenChange: (open) => {
        if (!open) {
          setTimeout(() => removeToast(id), ANIMATION_DURATION_MS);
        }
      }
    },
    /* @__PURE__ */ React65.createElement(SpaceBetween, { className: "items-center gap-2" }, /* @__PURE__ */ React65.createElement(FlexCenter, { className: "gap-2" }, icon, /* @__PURE__ */ React65.createElement(ToastPrimitives.Title, null, content)), (action || showClose) && /* @__PURE__ */ React65.createElement(FlexCenter, { className: "gap-2" }, action, showClose && /* @__PURE__ */ React65.createElement(ToastPrimitives.Close, { className: "flex items-center focus:outline-none" }, /* @__PURE__ */ React65.createElement(Close, { fill: "interactive-icon-primary-styles" }))))
  );
});

// src/components/Toast/ToastProvider.react.tsx
import React67, { useCallback as useCallback7, useMemo as useMemo10, useState as useState14 } from "react";

// src/components/Toast/Toaster.react.tsx
import * as ToastPrimitives2 from "@radix-ui/react-toast";
import React66 from "react";
var Toaster = ({ toasts }) => {
  const { toastYOffset, toastXOffset } = useToasts();
  return /* @__PURE__ */ React66.createElement(ToastPrimitives2.Provider, null, toasts.map((toast) => /* @__PURE__ */ React66.createElement(Toast, { key: toast.id, ...toast })), /* @__PURE__ */ React66.createElement(
    ToastPrimitives2.Viewport,
    {
      className: classNames(
        "fixed bottom-0 right-0 z-toast gap-2 p-4",
        "flex max-h-screen w-full flex-col-reverse items-center",
        "sm:top-auto sm:w-fit sm:max-w-[480px] sm:flex-col sm:items-end",
        "sm:px-6 lg:px-8 2xl:px-16",
        // Never use a right offset on mobile
        "xs-sm:!right-0"
      ),
      style: {
        bottom: toastYOffset,
        right: toastXOffset
      }
    }
  ));
};

// src/components/Toast/ToastProvider.react.tsx
var LONG_TOAST_TIMEOUT_MS = 6e3;
var SHORT_TOAST_TIMEOUT_MS = 2e3;
var count = 0;
var genId = () => {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
};
var ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState14([]);
  const [toastYOffset, setToastYOffset] = useState14();
  const [toastXOffset, setToastXOffset] = useState14();
  const resetToastYOffset = useCallback7(() => setToastYOffset(void 0), []);
  const resetToastXOffset = useCallback7(() => setToastXOffset(void 0), []);
  const addToasts = useCallback7(
    (toastElements) => setToasts((toasts2) => [
      ...toasts2,
      ...toastElements.map((t) => ({ ...t, id: genId() }))
    ]),
    []
  );
  const removeToast = useCallback7((id) => {
    setToasts((toasts2) => toasts2.filter((t) => t.id !== id));
  }, []);
  const showErrorMessage = useCallback7(
    (message, { timeout = LONG_TOAST_TIMEOUT_MS, ...rest } = {}) => addToasts([
      {
        content: message,
        icon: /* @__PURE__ */ React67.createElement(ErrorFilled, { fill: "error", size: 32 }),
        timeout,
        ...rest
      }
    ]),
    [addToasts]
  );
  const showSuccessMessage = useCallback7(
    (message, { timeout = LONG_TOAST_TIMEOUT_MS, ...rest } = {}) => addToasts([
      {
        content: message,
        icon: /* @__PURE__ */ React67.createElement(CheckCircleFilled, { fill: "green-2", size: 32 }),
        timeout,
        ...rest
      }
    ]),
    [addToasts]
  );
  const showWarningMessage = useCallback7(
    (message, { timeout = LONG_TOAST_TIMEOUT_MS, ...rest } = {}) => addToasts([
      {
        content: message,
        icon: /* @__PURE__ */ React67.createElement(WarningFilled, { fill: "yellow-1", size: 32 }),
        timeout,
        ...rest
      }
    ]),
    [addToasts]
  );
  const showInfoMessage = useCallback7(
    (message, { timeout = SHORT_TOAST_TIMEOUT_MS, ...rest } = {}) => addToasts([
      {
        content: message,
        icon: /* @__PURE__ */ React67.createElement(InfoFilled, { fill: "blue-3", size: 32 }),
        timeout,
        ...rest
      }
    ]),
    [addToasts]
  );
  const value = useMemo10(
    () => ({
      addToasts,
      removeToast,
      setToastXOffset,
      setToastYOffset,
      showErrorMessage,
      showInfoMessage,
      showSuccessMessage,
      showWarningMessage,
      toastXOffset,
      toastYOffset,
      resetToastXOffset,
      resetToastYOffset
    }),
    [
      addToasts,
      removeToast,
      showErrorMessage,
      showInfoMessage,
      showSuccessMessage,
      showWarningMessage,
      toastXOffset,
      toastYOffset,
      resetToastXOffset,
      resetToastYOffset
    ]
  );
  return /* @__PURE__ */ React67.createElement(ToastContext.Provider, { value }, children, /* @__PURE__ */ React67.createElement(Toaster, { toasts }));
};

// src/components/ToggleButtonGroup/ToggleButtonGroup.react.tsx
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import React68, { forwardRef as forwardRef43 } from "react";
var ToggleButtonGroupBase = forwardRef43(function ToggleButtonGroupBase2({ children, className, defaultValue, value, onValueChange, ...props }, ref) {
  return /* @__PURE__ */ React68.createElement(
    ToggleGroupPrimitive.Root,
    {
      asChild: true,
      defaultValue,
      ref,
      type: "single",
      value,
      onValueChange: (value2) => {
        if (value2) {
          onValueChange == null ? void 0 : onValueChange(value2);
        }
      },
      ...props
    },
    /* @__PURE__ */ React68.createElement(
      Flex,
      {
        className: classNames(
          "inline-flex [&>*]:rounded-none",
          "first:[&>*]:rounded-l last:[&>*]:rounded-r",
          "first:[&>*]:ml-0",
          className
        )
      },
      children
    )
  );
});
var ToggleButtonGroupButton = ({
  className,
  disabled,
  children,
  icon: IconComponent,
  value,
  ...props
}) => {
  return /* @__PURE__ */ React68.createElement(
    ToggleGroupPrimitive.Item,
    {
      className: classNames(
        "ml-[1] flex items-center rounded p-3 outline-none",
        "bg-component-gray-1 text-secondary transition-colors",
        "hover:text-primary focus-visible:text-primary",
        "data-[state=on]:bg-component-gray-3 data-[state=on]:text-primary",
        disabledVariants({ disabled }),
        className
      ),
      value,
      ...props
    },
    IconComponent ? /* @__PURE__ */ React68.createElement(IconComponent, null) : children
  );
};
var ToggleButtonGroup = Object.assign(ToggleButtonGroupBase, {
  Button: ToggleButtonGroupButton
});

// src/components/VirtualizedGrid/VirtualizedGrid.react.tsx
import {
  useVirtualizer,
  useWindowVirtualizer
} from "@tanstack/react-virtual";
import React70 from "react";

// src/components/VirtualizedGrid/constants.ts
var DEFAULT_OVERSCAN_BY = 2;
var DEFAULT_ITEM_HEIGHT_PX = 260;
var DEFAULT_MIN_ITEM_WIDTH = 180;
var DEFAULT_GRID_GAP = 12;
var DEFAULT_PAGINATION_THRESHOLD = 2;

// src/components/VirtualizedGrid/useGridRows.ts
var useGridRows = ({
  gridRef,
  minItemWidth,
  items,
  gridGap = 12
}) => {
  const gridWidth = useDimensions(gridRef).width;
  const availableSpace = gridWidth + gridGap;
  const totalSpacePerItem = minItemWidth + gridGap;
  const numItemsPerRow = Math.max(
    1,
    Math.floor(availableSpace / totalSpacePerItem)
  );
  const numRows = Math.ceil(items.length / numItemsPerRow);
  return { numItemsPerRow, numRows, gridWidth };
};

// src/components/VirtualizedGrid/VirtualizedGridView.react.tsx
import React69 from "react";

// src/hooks/useVirtualPagination/useVirtualPagination.ts
import { useEffect as useEffect10, useReducer, useRef as useRef9 } from "react";
if (typeof window !== "undefined") {
  import("./requestIdleCallback-47G2CSCS.mjs");
}
var useVirtualPagination = ({
  items,
  count: count2,
  loadNext,
  hasNextPage,
  paginationThreshold = 1
}) => {
  var _a;
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const isLoadingRef = useRef9(false);
  const idleCallbackRef = useRef9();
  const lastItemIndex = (_a = items[items.length - 1]) == null ? void 0 : _a.index;
  if (!isLoadingRef.current && hasNextPage && lastItemIndex !== void 0 && lastItemIndex >= count2 - paginationThreshold && loadNext) {
    isLoadingRef.current = true;
    Promise.resolve(loadNext()).then(() => {
      idleCallbackRef.current = requestIdleCallback(() => {
        idleCallbackRef.current = void 0;
        isLoadingRef.current = false;
        forceUpdate();
      });
    }).catch(() => {
      isLoadingRef.current = false;
    });
  }
  useEffect10(() => {
    return () => {
      if (idleCallbackRef.current) {
        cancelIdleCallback(idleCallbackRef.current);
      }
    };
  }, []);
  return isLoadingRef.current;
};

// src/components/VirtualizedGrid/VirtualizedGridView.react.tsx
var VirtualizedGridView = ({
  className,
  items,
  hasNext,
  loadNext,
  paginationThreshold = DEFAULT_PAGINATION_THRESHOLD,
  itemHeightEstimate = DEFAULT_ITEM_HEIGHT_PX,
  rowVirtualizer,
  renderItem: Item6,
  setGridRef,
  numItemsPerRow,
  numRows,
  gridGap,
  height,
  id,
  minItemWidth,
  itemKey,
  gridWidth
}) => {
  const virtualItems = rowVirtualizer.getVirtualItems();
  useVirtualPagination({
    items: virtualItems,
    count: numRows,
    loadNext,
    hasNextPage: hasNext,
    paginationThreshold
  });
  const gridStyle = {
    gridGap,
    gridTemplateColumns: `repeat(auto-fill, minmax(${minItemWidth}px, 1fr))`
  };
  return /* @__PURE__ */ React69.createElement(
    FlexColumn,
    {
      className: classNames("w-full", className),
      id,
      role: "grid",
      style: { height }
    },
    /* @__PURE__ */ React69.createElement("div", { className: "w-full flex-1 overflow-auto", ref: setGridRef }, !gridWidth ? /* @__PURE__ */ React69.createElement("div", { className: "grid grid-flow-row-dense", style: gridStyle }, items.map((item, i) => /* @__PURE__ */ React69.createElement(Item6, { item, key: itemKey(item, i) }))) : /* @__PURE__ */ React69.createElement(
      "div",
      {
        className: "relative",
        style: { height: rowVirtualizer.getTotalSize() }
      },
      virtualItems.map(({ index, key, start }) => {
        var _a;
        return /* @__PURE__ */ React69.createElement(
          "div",
          {
            className: "absolute left-0 top-0 w-full",
            "data-index": index,
            key,
            ref: rowVirtualizer.measureElement,
            role: "row",
            style: {
              transform: `translateY(${start - ((_a = rowVirtualizer.options.scrollMargin) != null ? _a : 0)}px)`,
              minHeight: itemHeightEstimate,
              paddingBottom: index < numRows - 1 ? gridGap : 0
            }
          },
          /* @__PURE__ */ React69.createElement(
            "div",
            {
              className: classNames("grid grid-flow-col-dense", className),
              style: gridStyle
            },
            items.slice(index * numItemsPerRow, (index + 1) * numItemsPerRow).map((item, i) => /* @__PURE__ */ React69.createElement(
              Item6,
              {
                item,
                key: itemKey(item, index * numItemsPerRow + i)
              }
            ))
          )
        );
      })
    ))
  );
};

// src/components/VirtualizedGrid/VirtualizedGrid.react.tsx
var isGridMode = (props) => {
  return props.scrollMode === "grid";
};
var VirtualizedGrid = (props) => {
  if (isGridMode(props)) {
    return /* @__PURE__ */ React70.createElement(GridScrollVirtualizedGrid, { ...props });
  }
  return /* @__PURE__ */ React70.createElement(WindowScrollVirtualizedGrid, { ...props });
};
var WindowScrollVirtualizedGrid = ({
  overscanBy = DEFAULT_OVERSCAN_BY,
  minItemWidth = DEFAULT_MIN_ITEM_WIDTH,
  itemHeightEstimate = DEFAULT_ITEM_HEIGHT_PX,
  gridGap = DEFAULT_GRID_GAP,
  gridBodyRef,
  items,
  initialRect,
  ...props
}) => {
  var _a;
  const [gridRef, setGridRef] = useCallbackRef(gridBodyRef);
  const { numItemsPerRow, numRows, gridWidth } = useGridRows({
    gridRef,
    items,
    minItemWidth,
    gridGap
  });
  const rowVirtualizer = useWindowVirtualizer({
    count: Math.ceil(items.length / numItemsPerRow),
    estimateSize: () => itemHeightEstimate,
    overscan: overscanBy,
    scrollMargin: (_a = gridRef.current) == null ? void 0 : _a.offsetTop,
    initialRect
  });
  return /* @__PURE__ */ React70.createElement(
    VirtualizedGridView,
    {
      gridGap,
      gridWidth,
      itemHeightEstimate,
      items,
      minItemWidth,
      numItemsPerRow,
      numRows,
      overscanBy,
      rowVirtualizer,
      setGridRef,
      ...props
    }
  );
};
var GridScrollVirtualizedGrid = ({
  overscanBy,
  minItemWidth = DEFAULT_MIN_ITEM_WIDTH,
  itemHeightEstimate = DEFAULT_ITEM_HEIGHT_PX,
  gridGap = DEFAULT_GRID_GAP,
  scrollContainerRef,
  gridBodyRef,
  items,
  initialRect,
  ...props
}) => {
  const [gridRef, setGridRef] = useCallbackRef(gridBodyRef);
  const { numItemsPerRow, numRows, gridWidth } = useGridRows({
    gridRef,
    items,
    minItemWidth,
    gridGap
  });
  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(items.length / numItemsPerRow),
    estimateSize: () => itemHeightEstimate,
    getScrollElement: () => {
      var _a;
      return (_a = scrollContainerRef == null ? void 0 : scrollContainerRef.current) != null ? _a : gridRef.current;
    },
    overscan: overscanBy,
    initialRect
  });
  return /* @__PURE__ */ React70.createElement(
    VirtualizedGridView,
    {
      gridGap,
      gridWidth,
      itemHeightEstimate,
      items,
      minItemWidth,
      numItemsPerRow,
      numRows,
      overscanBy,
      rowVirtualizer,
      setGridRef,
      ...props
    }
  );
};

// src/components/VirtualizedTable/VirtualizedTable.react.tsx
import {
  useVirtualizer as useVirtualizer2,
  useWindowVirtualizer as useWindowVirtualizer2
} from "@tanstack/react-virtual";
import React76, { useCallback as useCallback8 } from "react";

// src/components/VirtualizedTable/components/VirtualizedTableHeader.react.tsx
import React73, { forwardRef as forwardRef45 } from "react";

// src/components/VirtualizedTable/components/VirtualizedTableHeaderCell.react.tsx
import React72 from "react";

// src/components/VirtualizedTable/components/VirtualizedTableCell.react.tsx
import React71, { forwardRef as forwardRef44 } from "react";
import { tv as tv20 } from "tailwind-variants";
var virtualizedTableCellVariants = tv20({
  variants: {
    align: {
      left: "justify-start",
      center: "justify-center",
      right: "justify-end"
    }
  }
});
var VirtualizedTableCell = forwardRef44(function VirtualizedTableCell2({
  children,
  className,
  stickyLeft,
  role = "cell",
  align = "left",
  onClick
}, ref) {
  return /* @__PURE__ */ React71.createElement(
    FlexCenter,
    {
      className: classNames(
        "w-[128px] shrink-0 grow-0 overflow-hidden text-sm last:pr-4",
        stickyLeft !== void 0 && "sticky z-[1] h-full bg-base-1",
        virtualizedTableCellVariants({ align }),
        className
      ),
      ref,
      role,
      style: {
        left: stickyLeft
      },
      onClick
    },
    children
  );
});

// src/components/VirtualizedTable/components/VirtualizedTableHeaderCell.react.tsx
var getIconComponent = (sortOrder) => {
  switch (sortOrder) {
    case "asc":
      return ExpandLess;
    case "desc":
      return ExpandMore;
    case void 0:
      return ExpandAll;
  }
};
var SortIcon = ({ sortOrder }) => {
  const IconCompoennt = getIconComponent(sortOrder);
  return /* @__PURE__ */ React72.createElement(IconCompoennt, { size: 16 });
};
var VirtualizedTableHeaderCell = ({
  children,
  sort,
  className,
  ...cellProps
}) => {
  return /* @__PURE__ */ React72.createElement(
    VirtualizedTableCell,
    {
      className: classNames(sort && "cursor-pointer", className),
      role: "columnheader",
      onClick: () => sort == null ? void 0 : sort.onSort({
        column: sort.column,
        order: sort.order === "desc" ? "asc" : "desc"
      }),
      ...cellProps
    },
    /* @__PURE__ */ React72.createElement(Text, { color: (sort == null ? void 0 : sort.order) ? "primary" : "secondary", size: "small" }, children),
    sort && /* @__PURE__ */ React72.createElement(CenterAligned, null, /* @__PURE__ */ React72.createElement(SortIcon, { sortOrder: sort.order }))
  );
};

// src/components/VirtualizedTable/components/VirtualizedTableHeader.react.tsx
var VirtualizedTableHeaderBase = forwardRef45(function VirtualizedTableHeader({ className, children }, ref) {
  return /* @__PURE__ */ React73.createElement(
    "div",
    {
      className: classNames(
        "h-10 gap-4 border-b border-level-2 bg-base-1",
        // Ensure container spans the entire width of its contents to ensure sticky
        // columns do not lose stickiness on small viewports
        "inline-flex min-w-full",
        "overflow-auto",
        // Hide scrollbar
        "[-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden",
        className
      ),
      ref,
      role: "row"
    },
    children
  );
});
var VirtualizedTableHeader2 = Object.assign(
  VirtualizedTableHeaderBase,
  {
    Cell: VirtualizedTableHeaderCell
  }
);

// src/components/VirtualizedTable/components/VirtualizedTableRow.react.tsx
import React74, { forwardRef as forwardRef46 } from "react";
import { tv as tv21 } from "tailwind-variants";

// src/components/VirtualizedTable/constants.ts
var DEFAULT_OVERSCAN_BY2 = 1;
var DEFAULT_ITEM_HEIGHT_PX2 = 60;
var DEFAULT_PAGINATION_THRESHOLD2 = 6;

// src/components/VirtualizedTable/components/VirtualizedTableRow.react.tsx
var virtualizedTableRowVariants = tv21({
  variants: {
    interactive: {
      true: classNames(
        "cursor-pointer hover:bg-component-gray-1",
        "active:bg-component-gray-2 [&>*]:bg-transparent"
      )
    },
    expanded: {
      true: "bg-component-gray-1 [&>*]:bg-transparent"
    }
  }
});
var VirtualizedTableRowBase = forwardRef46(
  function VirtualizedTableRowBase2({
    className,
    children,
    minHeight = DEFAULT_ITEM_HEIGHT_PX2,
    more,
    onClick,
    interactive: paramInteractive,
    ...rest
  }, ref) {
    const interactive = paramInteractive != null ? paramInteractive : Boolean(onClick);
    const expanded = Boolean(more);
    return /* @__PURE__ */ React74.createElement(
      "div",
      {
        className: classNames("size-full min-w-fit"),
        ref,
        role: "row",
        style: { minHeight },
        onClick,
        ...rest
      },
      /* @__PURE__ */ React74.createElement(
        InlineFlex,
        {
          className: classNames(
            "min-w-full items-center gap-4",
            virtualizedTableRowVariants({ interactive, expanded }),
            className
          ),
          style: { height: minHeight }
        },
        children
      ),
      more && /* @__PURE__ */ React74.createElement(Flex, { className: "h-full rounded-b-xl bg-elevation-3" }, more)
    );
  }
);
var VirtualizedTableRow = Object.assign(VirtualizedTableRowBase, {
  Cell: VirtualizedTableCell
});

// src/components/VirtualizedTable/components/VirtualizedTableView.react.tsx
import React75, { useEffect as useEffect11, useMemo as useMemo11 } from "react";

// src/components/VirtualizedTable/utils.ts
var onScroll = (scrolledElement, otherElement) => {
  if (!scrolledElement || !otherElement) {
    return;
  }
  if (otherElement.scrollLeft !== scrolledElement.scrollLeft) {
    otherElement.scrollLeft = scrolledElement.scrollLeft;
  }
};
var addScrollEvent = (element, otherElement) => {
  let ignoreScroll = false;
  if (element && otherElement) {
    element.onscroll = () => {
      if (!ignoreScroll) {
        onScroll(element, otherElement);
        ignoreScroll = true;
      } else {
        ignoreScroll = false;
      }
    };
    otherElement.onscroll = () => {
      if (!ignoreScroll) {
        onScroll(otherElement, element);
        ignoreScroll = true;
      } else {
        ignoreScroll = false;
      }
    };
  }
};
var removeScrollEvent = (element) => {
  if (element) {
    element.onscroll = null;
  }
};

// src/components/VirtualizedTable/components/VirtualizedTableView.react.tsx
var VirtualizedTableView = ({
  className,
  items,
  hasNext,
  loadNext,
  paginationThreshold = DEFAULT_PAGINATION_THRESHOLD2,
  header,
  itemHeightEstimate = { min: DEFAULT_ITEM_HEIGHT_PX2 },
  renderRow: Row,
  rowVirtualizer,
  setTableRef,
  tableRef,
  height,
  id
}) => {
  const [headerRef, setHeaderRef] = useCallbackRef();
  useEffect11(() => {
    var _a;
    const header2 = (_a = headerRef.current) == null ? void 0 : _a.firstElementChild;
    const table = tableRef.current;
    if (header2 && table) {
      addScrollEvent(header2, table);
    }
    return () => {
      removeScrollEvent(header2);
      removeScrollEvent(table);
    };
  }, [headerRef, tableRef]);
  const virtualItems = rowVirtualizer.getVirtualItems();
  useVirtualPagination({
    items: virtualItems,
    count: items.length,
    loadNext,
    hasNextPage: hasNext,
    paginationThreshold
  });
  const rowRef = useMemo11(() => {
    const isExpandable = itemHeightEstimate.max !== void 0 && itemHeightEstimate.max > itemHeightEstimate.min;
    return isExpandable ? rowVirtualizer.measureElement : void 0;
  }, [itemHeightEstimate, rowVirtualizer.measureElement]);
  return /* @__PURE__ */ React75.createElement(
    FlexColumn,
    {
      "aria-rowcount": items.length,
      className: classNames("w-full", className),
      id,
      role: "table",
      style: { height }
    },
    header && // Display contents to allow header to become sticky with parent.
    /* @__PURE__ */ React75.createElement(
      "div",
      {
        className: "contents w-full flex-[none]",
        ref: setHeaderRef,
        role: "rowgroup"
      },
      header
    ),
    /* @__PURE__ */ React75.createElement(
      "div",
      {
        className: "w-full flex-1 overflow-auto",
        ref: setTableRef,
        role: "rowgroup"
      },
      /* @__PURE__ */ React75.createElement(
        "div",
        {
          className: "relative",
          style: { height: rowVirtualizer.getTotalSize() }
        },
        virtualItems.map(({ index, key, start }) => {
          var _a;
          return /* @__PURE__ */ React75.createElement(
            "div",
            {
              className: "absolute left-0 top-0 w-full",
              "data-index": index,
              key,
              ref: rowRef,
              role: "row",
              style: {
                transform: `translateY(${start - ((_a = rowVirtualizer.options.scrollMargin) != null ? _a : 0)}px)`,
                minHeight: itemHeightEstimate.min
              }
            },
            /* @__PURE__ */ React75.createElement(Row, { index, item: items[index] })
          );
        })
      )
    )
  );
};

// src/components/VirtualizedTable/VirtualizedTable.react.tsx
var isTableMode = (props) => {
  return props.scrollMode === "table";
};
var VirtualizedTableBase = ({ ...props }) => {
  if (isTableMode(props)) {
    return /* @__PURE__ */ React76.createElement(TableScrollVirtualizedTable, { ...props });
  }
  return /* @__PURE__ */ React76.createElement(WindowScrollVirtualizedTable, { ...props });
};
var WindowScrollVirtualizedTable = ({
  items,
  itemKey,
  overscanBy = DEFAULT_OVERSCAN_BY2,
  itemHeightEstimate = { min: DEFAULT_ITEM_HEIGHT_PX2 },
  tableBodyRef,
  initialRect,
  ...props
}) => {
  var _a;
  const [tableRef, setTableRef] = useCallbackRef(tableBodyRef);
  const getItemKey = useCallback8(
    (index) => {
      var _a2;
      return (_a2 = itemKey(items[index], index)) != null ? _a2 : index;
    },
    [itemKey, items]
  );
  const rowVirtualizer = useWindowVirtualizer2({
    count: items.length,
    estimateSize: () => {
      var _a2;
      return (_a2 = itemHeightEstimate.max) != null ? _a2 : itemHeightEstimate.min;
    },
    getItemKey,
    overscan: overscanBy,
    scrollMargin: (_a = tableRef.current) == null ? void 0 : _a.offsetTop,
    initialRect
  });
  return /* @__PURE__ */ React76.createElement(
    VirtualizedTableView,
    {
      itemHeightEstimate,
      items,
      rowVirtualizer,
      setTableRef,
      tableRef,
      ...props
    }
  );
};
var TableScrollVirtualizedTable = ({
  items,
  itemKey,
  overscanBy = DEFAULT_OVERSCAN_BY2,
  itemHeightEstimate = { min: DEFAULT_ITEM_HEIGHT_PX2 },
  scrollContainerRef,
  tableBodyRef,
  initialRect,
  ...props
}) => {
  const [tableRef, setTableRef] = useCallbackRef(tableBodyRef);
  const getItemKey = useCallback8(
    (index) => {
      var _a;
      return (_a = itemKey(items[index], index)) != null ? _a : index;
    },
    [itemKey, items]
  );
  const rowVirtualizer = useVirtualizer2({
    count: items.length,
    estimateSize: () => {
      var _a;
      return (_a = itemHeightEstimate.max) != null ? _a : itemHeightEstimate.min;
    },
    getItemKey,
    getScrollElement: () => {
      var _a;
      return (_a = scrollContainerRef == null ? void 0 : scrollContainerRef.current) != null ? _a : tableRef.current;
    },
    overscan: overscanBy,
    initialRect
  });
  return /* @__PURE__ */ React76.createElement(
    VirtualizedTableView,
    {
      itemHeightEstimate,
      items,
      rowVirtualizer,
      setTableRef,
      tableRef,
      ...props
    }
  );
};
var VirtualizedTable = Object.assign(VirtualizedTableBase, {
  Header: VirtualizedTableHeader2,
  Row: VirtualizedTableRow
});

// src/components/List/List.react.tsx
import React79, { forwardRef as forwardRef47 } from "react";
import { tv as tv22 } from "tailwind-variants";

// src/components/List/ListGroup.react.tsx
import React77 from "react";
var ListGroup = ({ groupLabel, children }) => {
  return /* @__PURE__ */ React77.createElement("div", null, /* @__PURE__ */ React77.createElement("div", { className: "px-4 py-3" }, /* @__PURE__ */ React77.createElement(Text, { weight: "semibold" }, groupLabel)), children);
};

// src/components/List/ListHeader.react.tsx
import React78 from "react";
var ListHeaderBase = ({ className, ...rest }) => {
  const { ref, isSticky } = useIsSticky();
  return /* @__PURE__ */ React78.createElement(
    SpaceBetween,
    {
      className: classNames(
        "sticky top-[-1px] border-b border-level-2 bg-inherit p-4",
        isSticky && "shadow-elevation-1",
        className
      ),
      ref,
      ...rest
    }
  );
};
var ListHeaderTitle = ({
  size = "tiny",
  weight = "semibold",
  ...rest
}) => {
  return /* @__PURE__ */ React78.createElement(Text, { color: "secondary", size, weight, ...rest });
};
var ListHeader = Object.assign(ListHeaderBase, {
  Title: ListHeaderTitle
});

// src/components/List/List.react.tsx
var listVariants = tv22({
  variants: {
    showBorder: {
      true: classNames(
        "border border-level-2",
        "[&>:not(:last-child)]:!border-b [&>:not(:last-child)]:border-b-level-2"
      )
    },
    variant: {
      default: "rounded-xl bg-base-1",
      condensed: classNames(
        "rounded-xl bg-base-1",
        twDataIdSelector(Item2, "[&_[data-id=Item]]:px-4"),
        twDataIdSelector(Item2, "[&_[data-id=Item]]:py-2"),
        twDataIdSelector(Item2.Title, "[&_[data-id=ItemTitle]]:font-normal"),
        twDataIdSelector(Item2.Content, "[&_[data-id=ItemContent]]:mr-0")
      ),
      // This variant is used within a container that already has border styles
      framed: classNames(
        "rounded-none border-0 bg-inherit",
        twDataIdSelector(Item2, "[&_[data-id=Item]]:rounded-none")
      )
    }
  }
});
var ListBase = forwardRef47(function List({
  className,
  children,
  height,
  showBorder = true,
  variant = "default",
  ...rest
}, ref) {
  return /* @__PURE__ */ React79.createElement(
    "ul",
    {
      className: classNames(
        "[&>:first-child]:rounded-t-xl [&>:last-child]:rounded-b-xl",
        "[&>:first-child>*:first-child]:rounded-t-xl [&>:last-child>*:last-child]:rounded-b-xl",
        twDataIdSelector(Item2, "[&_[data-id=Item]]:border-0"),
        twDataIdSelector(Item2, "[&_[data-id=Item]]:overflow-hidden"),
        listVariants({ showBorder, variant }),
        className
      ),
      ref,
      style: {
        height: height ? `${height}px` : void 0,
        // If a height is set, content should scroll within the container
        overflow: height ? "auto" : void 0
      },
      ...rest
    },
    children
  );
});
var ListItem = Object.assign(
  forwardRef47(function ListItem2({ asChild, children, ...props }, ref) {
    return /* @__PURE__ */ React79.createElement("li", { className: "w-full" }, /* @__PURE__ */ React79.createElement(Item2, { asChild, ...props, ref }, children));
  }),
  {
    Avatar: Item2.Avatar,
    Content: Item2.Content,
    Title: Item2.Title,
    Description: Item2.Description,
    Side: Item2.Side,
    Action: Item2.Action
  }
);
var ListItemValue = ({
  weight = "semibold",
  ...rest
}) => {
  return /* @__PURE__ */ React79.createElement(Text, { weight, ...rest });
};
var List2 = Object.assign(ListBase, {
  Item: Object.assign(ListItem, { Value: ListItemValue }),
  Header: ListHeader,
  Group: ListGroup
});

// src/providers/ThemeProvider/ThemeProvider.react.tsx
import React80, { createContext as createContext9, useContext as useContext5, useMemo as useMemo12, useState as useState15 } from "react";

// src/theme.ts
import { rgba } from "polished";

// src/constants/colors.ts
var COLORS = {
  white: "#ffffff",
  black: "#000000",
  // Grays
  gray1: "#b3b3b3",
  // gray
  gray2: "#545454",
  // dark gray
  gray3: "#121212",
  // smoke
  // Blues
  blue1: "#8FC7FF",
  // basin
  blue2: "#2E8DEE",
  // gulf
  blue3: "#2081E2",
  // sea blue
  blue4: "#1868B7",
  // dark sea blue
  blue5: "#235AA3",
  // offshore
  // Greens
  green1: "#68F2AB",
  // light seagrass
  green2: "#34C77B",
  // seagrass
  green3: "#0C954E",
  // dark seagrass
  // Yellows
  yellow1: "#F6C000",
  // starfish
  yellow2: "#C59E10",
  // dark starfish
  // Reds
  red1: "#EB6B6B",
  // light coral
  red2: "#EB5757",
  // coral
  red3: "#E62929",
  // dark coral
  // Accent colors
  accentRed: "#E62929",
  // dark coral
  accentPurple: "#5D32E9",
  // octopus
  accentFuscia: "#E932BE",
  // pink fish
  accentOrange: "#F2994A",
  // seahorse
  accentCerulean: "#15B2E5",
  // marina blue
  accentAqua: "#2BCDE4"
  // aqua
};

// src/constants/typefaces.ts
var TYPEFACES = {
  display: '"Poppins", sans-serif',
  heading: '"Poppins", sans-serif',
  body: '"Inter", sans-serif'
};

// src/theme.ts
var lightTheme = {
  colors: {
    base1: COLORS.white,
    base2: "#FDFDFD",
    components: {
      background: {
        transparent: "transparent",
        gray1: rgba(COLORS.gray3, 0.04),
        gray2: rgba(COLORS.gray3, 0.08),
        gray3: rgba(COLORS.gray3, 0.12)
      },
      border: {
        level1: rgba(COLORS.gray3, 0.08),
        level2: rgba(COLORS.gray3, 0.12),
        level3: rgba(COLORS.gray3, 0.32)
      },
      elevation: {
        level1: {
          subtle: {
            shadow: `0px 4px 16px ${rgba(COLORS.black, 0.04)}`,
            background: COLORS.white
          },
          regular: {
            shadow: `0px 4px 16px ${rgba(COLORS.black, 0.08)}`,
            background: COLORS.white
          }
        },
        level2: {
          shadow: `0px 6px 24px ${rgba(COLORS.black, 0.12)}`,
          background: COLORS.white
        },
        level3: {
          shadow: `0px 6px 32px ${rgba(COLORS.black, 0.2)}`,
          background: COLORS.white
        }
      }
    },
    text: {
      primary: COLORS.gray3,
      secondary: COLORS.gray2,
      interactive: {
        primary: COLORS.blue3,
        primaryHover: COLORS.blue5,
        secondary: COLORS.gray3,
        secondaryHover: COLORS.gray2
      }
    },
    icons: {
      primary: COLORS.gray3,
      primaryHover: COLORS.gray2,
      secondary: COLORS.gray2,
      secondaryHover: COLORS.gray3
    },
    ...COLORS
  },
  typeface: TYPEFACES
};
var darkTheme = {
  colors: {
    base1: COLORS.gray3,
    base2: COLORS.gray3,
    components: {
      background: {
        transparent: "transparent",
        gray1: rgba(COLORS.white, 0.04),
        gray2: rgba(COLORS.white, 0.08),
        gray3: rgba(COLORS.white, 0.12)
      },
      border: {
        level1: rgba(COLORS.white, 0.08),
        level2: rgba(COLORS.white, 0.12),
        level3: rgba(COLORS.white, 0.32)
      },
      elevation: {
        level1: {
          subtle: {
            shadow: `0px 4px 16px ${rgba(COLORS.black, 0.08)}`,
            background: `#202020`
          },
          regular: {
            shadow: `0px 4px 16px ${rgba(COLORS.black, 0.08)}`,
            background: `#202020`
          }
        },
        level2: {
          shadow: `0px 6px 24px ${rgba(COLORS.black, 0.12)}`,
          background: `#252525`
        },
        level3: {
          shadow: `0px 6px 32px ${rgba(COLORS.black, 0.2)}`,
          background: `#2E2E2E`
        }
      }
    },
    text: {
      primary: COLORS.white,
      secondary: COLORS.gray1,
      interactive: {
        primary: COLORS.blue1,
        primaryHover: COLORS.blue3,
        secondary: COLORS.white,
        secondaryHover: COLORS.gray1
      }
    },
    icons: {
      primary: COLORS.white,
      primaryHover: COLORS.gray1,
      secondary: COLORS.gray1,
      secondaryHover: COLORS.white
    },
    ...COLORS
  },
  typeface: TYPEFACES
};
var getTheme = (theme) => {
  return theme === "dark" ? darkTheme : lightTheme;
};

// src/providers/ThemeProvider/ThemeProvider.react.tsx
var DEFAULT_THEME = "light";
var ThemeContext = createContext9({
  theme: DEFAULT_THEME,
  setTheme: () => null
});
var setThemeControlled = () => {
  if (process.env.NODE_ENV === "development") {
    throw new Error(
      "You are trying to setTheme in a controlled mode. This is a noop/redundant operation."
    );
  }
};
var ThemeProvider = ({
  children,
  initialTheme = DEFAULT_THEME,
  theme: controlledTheme
}) => {
  const [theme, setTheme] = useState15(initialTheme);
  const value = useMemo12(() => {
    return controlledTheme ? { theme: controlledTheme, setTheme: setThemeControlled } : { theme, setTheme };
  }, [theme, controlledTheme]);
  return /* @__PURE__ */ React80.createElement(ThemeContext.Provider, { value }, children);
};
var useTheme = () => {
  return useContext5(ThemeContext);
};
var useThemeValues = () => {
  const { theme } = useTheme();
  return getTheme(theme);
};
export {
  AspectRatio,
  AuthCode,
  Carousel2 as Carousel,
  Checkbox,
  Collapsible,
  Combobox2 as Combobox,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxItemAvatar,
  ComboboxItemContent,
  ComboboxItemDescription,
  ComboboxItemSide,
  ComboboxItemTitle,
  ComboboxList,
  ComboboxShortcut,
  Command2 as Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem2 as CommandItem,
  CommandItemAvatar,
  CommandItemContent,
  CommandItemDescription,
  CommandItemSide,
  CommandItemTitle,
  CommandList,
  CommandShortcut,
  Dropdown2 as Dropdown,
  DropdownItem2 as DropdownItem,
  DropdownItemAvatar,
  DropdownItemContent,
  DropdownItemDescription,
  DropdownItemTitle,
  DropdownSeparator,
  FloatingCombobox2 as FloatingCombobox,
  FloatingComboboxEmpty,
  FloatingComboboxInput,
  FloatingComboboxItem,
  FloatingComboboxItemAvatar,
  FloatingComboboxItemContent,
  FloatingComboboxItemDescription,
  FloatingComboboxItemTitle,
  FloatingComboboxList,
  FormControl,
  Input,
  InputGroup,
  InputGroupInput,
  Label,
  List2 as List,
  ListGroup,
  ListHeader,
  ListHeaderTitle,
  ListItem,
  ListItemValue,
  Media,
  MediaContextProvider,
  Modal2 as Modal,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
  ModalHeader,
  ModalHeaderTitle,
  PhoneInput,
  Popover,
  Progress,
  RadioButtonGroup,
  RadioButtonGroupItemContent,
  RadioButtonGroupItemDescription,
  RadioButtonGroupItemIcon,
  RadioGroup,
  RadioGroupItem,
  ScrollingPaginator,
  Select2 as Select,
  SelectButton,
  SelectItem2 as SelectItem,
  SelectItemAvatar,
  SelectItemContent,
  SelectItemDescription,
  SelectItemTitle,
  Separator,
  Sheet2 as Sheet,
  SheetBody,
  SheetFooter,
  SheetFooterButton,
  SheetHeader,
  SheetSubtitle,
  SheetTitle,
  Sidebar2 as Sidebar,
  SidebarAvatar,
  SidebarItem2 as SidebarItem,
  SidebarItemTitle,
  SizeProvider,
  Slider,
  Switch,
  TextOverflow,
  ThemeProvider,
  Toast,
  ToastContext,
  ToastProvider,
  ToggleButtonGroup,
  Tooltip,
  VirtualizedGrid,
  VirtualizedTable,
  getBreakpointPixelValue,
  mediaStyles,
  useCallbackRef,
  useDimensions,
  useIsGreaterThanOrEqualToLg,
  useIsHydrated,
  useIsLessThan2Xl,
  useIsLessThan3Xl,
  useIsLessThan4Xl,
  useIsLessThanBreakpoint,
  useIsLessThanLg,
  useIsLessThanMax,
  useIsLessThanMd,
  useIsLessThanSm,
  useIsLessThanXl,
  useIsMountedRef,
  useIsSticky,
  useIsomorphicLayoutEffect,
  useItemsWithSkeletons,
  useTheme,
  useThemeValues,
  useToasts
};
//# sourceMappingURL=index.mjs.map